import { useState, useEffect, useCallback, useRef } from "react";
import { auth, clientes, mensajes, columnas, campos, usuarios, fuentes, seguimientos, stats } from "./crm-services.js";

// ─── CONFIG PANEL (integrado) ─────────────────────────────────────────────────

const SECTION_ACCESS = {
  cuentas:         ["admin", "manager"],
  columnas:        ["admin", "manager"],
  fuentes:         ["admin", "manager"],
  disparadores:    ["admin", "manager"],
  identificadores: ["admin"],
  tarjetas:        ["admin", "manager"],
  permisos:        ["admin"],
  general:         ["admin"],
};

const CONFIG_SECTIONS = [
  { id:"cuentas",         icon:"👥", label:"Cuentas"         },
  { id:"columnas",        icon:"📋", label:"Columnas"        },
  { id:"fuentes",         icon:"🔌", label:"Fuentes"         },
  { id:"disparadores",    icon:"⚡", label:"Disparadores"    },
  { id:"identificadores", icon:"🏷", label:"Identificadores" },
  { id:"tarjetas",        icon:"🃏", label:"Tarjetas"        },
  { id:"permisos",        icon:"📊", label:"Permisos estad." },
  { id:"general",         icon:"⚙", label:"General"         },
];

const CONFIG_COLORS = [
  // Rojos
  "#fca5a5","#f87171","#ef4444","#dc2626",
  // Naranjas
  "#fdba74","#fb923c","#f97316","#ea580c",
  // Amarillos
  "#fde047","#facc15","#eab308","#ca8a04",
  // Verdes
  "#86efac","#4ade80","#22c55e","#16a34a",
  // Turquesas
  "#67e8f9","#22d3ee","#06b6d4","#0891b2",
  // Azules
  "#93c5fd","#60a5fa","#3b82f6","#2563eb",
  // Violetas
  "#c4b5fd","#a78bfa","#8b5cf6","#7c3aed",
  // Fucsias
  "#f0abfc","#e879f9","#d946ef","#c026d3",
  // Rosas
  "#fda4af","#fb7185","#f43f5e","#e11d48",
  // Grises
  "#cbd5e1","#94a3b8","#64748b","#475569",
];
const PLATFORM_ICONS  = { whatsapp:"💬", messenger:"💙", instagram:"📷", telegram:"✈️" };
const PLATFORM_COLORS = { whatsapp:"#25d366", messenger:"#0084ff", instagram:"#e1306c", telegram:"#229ED9" };
const AGENCIES_LIST   = ["Rs Viajes Periodismo","Rs Viajes Independencia","Rs Viajes Centro"];
const AGENCY_IDS      = { "Rs Viajes Periodismo": 1, "Rs Viajes Independencia": 2, "Rs Viajes Centro": 3 };

// Permisos de estadísticas por rol — configurable desde el panel
const INIT_STAT_PERMS = [
  {id:"ranking_ventas",   label:"Ranking de ventas",           admin:true,manager:true,supervisor:true, agente:true },
  {id:"tiempo_venta",     label:"Tiempo medio de venta",       admin:true,manager:true,supervisor:true, agente:true },
  {id:"leads_recibidos",  label:"Leads recibidos",             admin:true,manager:true,supervisor:true, agente:false},
  {id:"conversion",       label:"Conversión por agencia",      admin:true,manager:true,supervisor:true, agente:false},
  {id:"destinos",         label:"Destinos más solicitados",    admin:true,manager:true,supervisor:true, agente:false},
  {id:"canales",          label:"Leads por canal",             admin:true,manager:true,supervisor:true, agente:false},
  {id:"tiempo_respuesta", label:"Tiempo de primera respuesta", admin:true,manager:true,supervisor:true, agente:true },
  {id:"seguimientos",     label:"Seguimientos completados",    admin:true,manager:true,supervisor:true, agente:true },
];

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  // Brand
  primary:    "#f97316",   // orange-500
  primaryDk:  "#ea580c",   // orange-600
  primaryLt:  "#fff7ed",   // orange-50
  accent:     "#fb923c",   // orange-400
  // Sidebar gradient
  sidebarTop: "#f97316",
  sidebarBot: "#dc2626",
  // Surfaces
  bg:         "#fafaf9",   // warm off-white
  card:       "#ffffff",
  panel:      "#f5f5f4",   // stone-100
  border:     "#e7e5e4",   // stone-200
  borderDk:   "#d6d3d1",
  // Text
  textPrimary:"#1c1917",   // stone-900
  textSub:    "#78716c",   // stone-500
  textMuted:  "#a8a29e",   // stone-400
  // Misc
  indigo:     "#6366f1",
  indigoBg:   "#eef2ff",
};

function CfgToggle({ value, onChange, label }) {
  return (
    <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
      <div onClick={() => onChange(!value)} style={{ width:36, height:20, borderRadius:10, background:value?T.primary:"#e7e5e4", position:"relative", transition:"background 0.2s", flexShrink:0 }}>
        <div style={{ width:16, height:16, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:value?18:2, transition:"left 0.2s", boxShadow:"0 1px 3px rgba(0,0,0,0.2)" }} />
      </div>
      {label && <span style={{ fontSize:12, color:T.textSub }}>{label}</span>}
    </label>
  );
}

function CfgModal({ children, onClose }) {
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(28,25,23,0.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, backdropFilter:"blur(6px)" }}>
      <div onClick={e => e.stopPropagation()} style={{ background:"#fff", borderRadius:20, padding:28, width:460, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 24px 64px rgba(0,0,0,0.18)", border:`1px solid ${T.border}` }}>
        {children}
      </div>
    </div>
  );
}

function CfgBadge({ label, color }) {
  return <span style={{ fontSize:10, padding:"2px 8px", borderRadius:20, fontWeight:700, background:color+"22", color, flexShrink:0 }}>{label}</span>;
}

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return <button onClick={() => { navigator.clipboard?.writeText(String(text)); setCopied(true); setTimeout(()=>setCopied(false),1500); }} style={{ fontSize:10, padding:"2px 8px", borderRadius:4, border:"1px solid #e2e8f0", background:copied?"#dcfce7":"#f8fafc", color:copied?"#059669":"#64748b", cursor:"pointer", fontWeight:600, flexShrink:0 }}>{copied?"✓":"Copiar"}</button>;
}

// ─── DISPARADORES ─────────────────────────────────────────────────────────────

const CONDICION_TIPOS = [
  { id: "columna_entrada",     label: "Cuando se mueve a esta columna",              hasTime: false },
  { id: "tiempo_sin_cliente",  label: "X horas desde último mensaje del cliente",    hasTime: true  },
  { id: "tiempo_sin_agente",   label: "X horas desde último mensaje del agente",     hasTime: true  },
  { id: "tiempo_en_columna",   label: "X días en esta columna sin moverse",          hasTime: true  },
  { id: "mensaje_cliente",     label: "Cuando el cliente envía un mensaje",          hasTime: false },
  { id: "mensaje_agente",      label: "Cuando el agente envía un mensaje",           hasTime: false },
  { id: "campo_cambia",        label: "Cuando cambia el valor de un campo",          hasTime: false },
  { id: "fecha_campo",         label: "X días antes/después de la fecha de un campo", hasTime: true },
];

const ACCION_TIPOS = [
  { id: "mover_columna",       label: "Mover cliente a otra columna"   },
  { id: "cambiar_campo",       label: "Cambiar valor de un campo"      },
  { id: "webhook_n8n",         label: "Enviar webhook a n8n"           },
  { id: "notificar_agente",    label: "Notificar al agente asignado"   },
];

const DISPARADOR_EMPTY = {
  id: null, nombre: "", activo: true,
  condicion: { tipo: "columna_entrada", horas: 24, campo_id: null, valor: "" },
  accion:    { tipo: "webhook_n8n",     columna_destino: null, campo_id: null, valor: "", webhook_url: "", mensaje: "" },
};

// Formulario de disparador — componente independiente con estado local para evitar pérdida de foco
function DispForm({ initialData, allCols, onSave, onCancel, saveLabel }) {
  const [data, setData] = useState({
    ...initialData,
    condicion: {...(initialData.condicion || {tipo:"columna_entrada", horas:24})},
    accion:    {...(initialData.accion    || {tipo:"webhook_n8n", webhook_url:""})}
  });
  const cond     = data.condicion || {};
  const acc      = data.accion    || {};
  const condTipo = CONDICION_TIPOS.find(t => t.id === cond.tipo) || CONDICION_TIPOS[0];

  return (
    <div style={{ background:"#f8fafc", borderRadius:12, padding:20, border:"1px solid #e2e8f0", marginBottom:12 }}>
      <div style={{ marginBottom:16 }}>
        <label style={{ fontSize:11, fontWeight:700, color:"#64748b", display:"block", marginBottom:6 }}>NOMBRE DEL DISPARADOR</label>
        <input value={data.nombre||""} onChange={e=>setData(p=>({...p,nombre:e.target.value}))} placeholder="Ej: Seguimiento 24h sin respuesta" style={{ width:"100%", padding:"9px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:13, outline:"none", boxSizing:"border-box" }} />
      </div>

      {/* CONDICIÓN */}
      <div style={{ background:"#fff", borderRadius:10, padding:14, border:"1px solid #e2e8f0", marginBottom:12 }}>
        <p style={{ fontSize:11, fontWeight:800, color:"#6366f1", margin:"0 0 10px", textTransform:"uppercase", letterSpacing:0.5 }}>⚡ Condición</p>
        <div style={{ marginBottom:10 }}>
          <select value={cond.tipo||"columna_entrada"} onChange={e=>setData(p=>({...p,condicion:{...p.condicion,tipo:e.target.value}}))} style={{ width:"100%", padding:"9px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:13, outline:"none" }}>
            {CONDICION_TIPOS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        {condTipo.hasTime && (
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <input type="number" min={1} value={cond.horas||24} onChange={e=>setData(p=>({...p,condicion:{...p.condicion,horas:Number(e.target.value)}}))} style={{ width:80, padding:"8px 10px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:13, outline:"none" }} />
            <span style={{ fontSize:12, color:"#64748b" }}>{cond.tipo==="tiempo_en_columna"?"días":"horas"}</span>
          </div>
        )}
        {(cond.tipo==="campo_cambia" || cond.tipo==="fecha_campo") && (
          <div style={{ marginTop:8 }}>
            <label style={{ fontSize:11, color:"#64748b", display:"block", marginBottom:4 }}>ID del campo</label>
            <input value={cond.campo_id||""} onChange={e=>setData(p=>({...p,condicion:{...p.condicion,campo_id:e.target.value}}))} placeholder="Ej: 2004115" style={{ width:"100%", padding:"8px 10px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:13, outline:"none", boxSizing:"border-box" }} />
          </div>
        )}
      </div>

      {/* ACCIÓN */}
      <div style={{ background:"#fff", borderRadius:10, padding:14, border:"1px solid #e2e8f0", marginBottom:14 }}>
        <p style={{ fontSize:11, fontWeight:800, color:"#10b981", margin:"0 0 10px", textTransform:"uppercase", letterSpacing:0.5 }}>🎯 Acción</p>
        <div style={{ marginBottom:10 }}>
          <select value={acc.tipo||"webhook_n8n"} onChange={e=>setData(p=>({...p,accion:{...p.accion,tipo:e.target.value}}))} style={{ width:"100%", padding:"9px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:13, outline:"none" }}>
            {ACCION_TIPOS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        {acc.tipo==="mover_columna" && (
          <div>
            <label style={{ fontSize:11, color:"#64748b", display:"block", marginBottom:4 }}>Columna destino</label>
            <select value={acc.columna_destino||""} onChange={e=>setData(p=>({...p,accion:{...p.accion,columna_destino:Number(e.target.value)}}))} style={{ width:"100%", padding:"9px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:13, outline:"none" }}>
              <option value="">Seleccionar columna</option>
              {allCols.map(c => <option key={c.id} value={c.id}>{c.agency?.replace("Rs Viajes ","") || ""} · {c.title}</option>)}
            </select>
          </div>
        )}
        {acc.tipo==="cambiar_campo" && (
          <div style={{ display:"flex", gap:8 }}>
            <input value={acc.campo_id||""} onChange={e=>setData(p=>({...p,accion:{...p.accion,campo_id:e.target.value}}))} placeholder="ID del campo" style={{ flex:1, padding:"8px 10px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, outline:"none" }} />
            <input value={acc.valor||""} onChange={e=>setData(p=>({...p,accion:{...p.accion,valor:e.target.value}}))} placeholder="Nuevo valor" style={{ flex:1, padding:"8px 10px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, outline:"none" }} />
          </div>
        )}
        {acc.tipo==="webhook_n8n" && (
          <div>
            <label style={{ fontSize:11, color:"#64748b", display:"block", marginBottom:4 }}>URL del webhook en n8n</label>
            <input value={acc.webhook_url||""} onChange={e=>setData(p=>({...p,accion:{...p.accion,webhook_url:e.target.value}}))} placeholder="https://tu-n8n.com/webhook/..." style={{ width:"100%", padding:"8px 10px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, outline:"none", boxSizing:"border-box" }} />
          </div>
        )}
        {acc.tipo==="notificar_agente" && (
          <div>
            <label style={{ fontSize:11, color:"#64748b", display:"block", marginBottom:4 }}>Mensaje de notificación</label>
            <textarea value={acc.mensaje||""} onChange={e=>setData(p=>({...p,accion:{...p.accion,mensaje:e.target.value}}))} placeholder="Ej: El cliente lleva 24h sin responder" rows={2} style={{ width:"100%", padding:"8px 10px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, outline:"none", resize:"none", fontFamily:"inherit", boxSizing:"border-box" }} />
          </div>
        )}
      </div>

      <div style={{ display:"flex", gap:8 }}>
        <button onClick={() => onSave(data)} style={{ flex:1, padding:"10px", background:"#6366f1", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer" }}>{saveLabel}</button>
        <button onClick={onCancel} style={{ flex:1, padding:"10px", background:"#f1f5f9", color:"#475569", border:"none", borderRadius:8, fontSize:13, cursor:"pointer" }}>Cancelar</button>
      </div>
    </div>
  );
}

function DisparadoresPanel({ cfgColumns }) {
  const [activeAgency, setActiveAgency] = useState(AGENCIES_LIST[0]);
  const [activeColId, setActiveColId]   = useState(null);
  const [disparadores, setDisparadores] = useState({});
  const [editando, setEditando]         = useState(null);
  const [showNew, setShowNew]           = useState(false);
  const [copyMsg, setCopyMsg]           = useState("");

  const agencyCols = cfgColumns.filter(c => c.agency === activeAgency).sort((a,b) => a.order - b.order);
  const activeCol  = agencyCols.find(c => c.id === activeColId) || agencyCols[0];
  const colDisps   = disparadores[activeCol?.id] || [];
  const allCols    = cfgColumns;

  const emptyDisp = () => ({ id: null, nombre: "", activo: true, condicion: {tipo:"columna_entrada", horas:24}, accion: {tipo:"webhook_n8n", webhook_url:"", columna_destino:null, campo_id:null, valor:"", mensaje:""} });

  const addDisp = (disp) => {
    const nuevo = {...disp, id: Date.now()};
    setDisparadores(prev => ({...prev, [activeCol.id]: [...(prev[activeCol.id]||[]), nuevo]}));
    setShowNew(false);
  };

  const updateDisp = (disp) => {
    setDisparadores(prev => ({...prev, [activeCol.id]: (prev[activeCol.id]||[]).map(d => d.id===disp.id?disp:d)}));
    setEditando(null);
  };

  const deleteDisp = (id) => {
    if (!window.confirm("¿Eliminar este disparador?")) return;
    setDisparadores(prev => ({...prev, [activeCol.id]: (prev[activeCol.id]||[]).filter(d => d.id!==id)}));
  };

  const toggleDisp = (id) => {
    setDisparadores(prev => ({...prev, [activeCol.id]: (prev[activeCol.id]||[]).map(d => d.id===id?{...d,activo:!d.activo}:d)}));
  };

  const copyTo = (disp, targetColId) => {
    const nuevo = {...disp, id: Date.now()};
    setDisparadores(prev => ({...prev, [targetColId]: [...(prev[targetColId]||[]), nuevo]}));
    setCopyMsg(`Copiado a ${cfgColumns.find(c=>c.id===targetColId)?.title || targetColId}`);
    setTimeout(() => setCopyMsg(""), 2000);
  };

  return (
    <div style={{ display:"flex", gap:0, height:"600px", overflow:"hidden", margin:"-24px" }}>
      {/* Sidebar */}
      <div style={{ width:220, flexShrink:0, borderRight:"1px solid #e2e8f0", display:"flex", flexDirection:"column", background:"#f8fafc", overflowY:"auto" }}>
        {AGENCIES_LIST.map(agency => (
          <div key={agency}>
            <button onClick={() => setActiveAgency(agency)} style={{ width:"100%", padding:"10px 14px", border:"none", background:"transparent", textAlign:"left", fontSize:11, fontWeight:800, color:activeAgency===agency?"#6366f1":"#94a3b8", textTransform:"uppercase", letterSpacing:0.5, cursor:"pointer", borderBottom:"1px solid #f1f5f9" }}>
              {agency.replace("Rs Viajes ","")}
            </button>
            {activeAgency===agency && cfgColumns.filter(c=>c.agency===agency).sort((a,b)=>a.order-b.order).map(col => (
              <button key={col.id} onClick={() => setActiveColId(col.id)} style={{ width:"100%", padding:"9px 14px 9px 22px", border:"none", background:(activeCol?.id===col.id)?"#eef2ff":"transparent", textAlign:"left", fontSize:12, color:(activeCol?.id===col.id)?"#6366f1":"#475569", cursor:"pointer", display:"flex", alignItems:"center", gap:8, borderBottom:"1px solid #f1f5f9" }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:col.color, flexShrink:0 }} />
                <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{col.title}</span>
                {(disparadores[col.id]||[]).length > 0 && <span style={{ fontSize:9, padding:"1px 5px", borderRadius:10, background:"#6366f122", color:"#6366f1", fontWeight:700 }}>{(disparadores[col.id]||[]).length}</span>}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Panel principal */}
      <div style={{ flex:1, overflowY:"auto", padding:24 }}>
        {activeCol ? (
          <>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:12, height:12, borderRadius:"50%", background:activeCol.color }} />
                <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:"#1e293b" }}>{activeCol.title}</h3>
                <span style={{ fontSize:11, color:"#94a3b8" }}>{activeCol.agency?.replace("Rs Viajes ","")}</span>
              </div>
              <button onClick={() => { setShowNew(true); setEditando(null); }} style={{ padding:"8px 16px", background:"#6366f1", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer" }}>+ Nuevo disparador</button>
            </div>

            {copyMsg && <div style={{ padding:"10px 16px", background:"#dcfce7", borderRadius:8, fontSize:12, color:"#059669", fontWeight:700, marginBottom:12 }}>✓ {copyMsg}</div>}

            {showNew && <DispForm key="new" initialData={emptyDisp()} allCols={allCols} onSave={addDisp} onCancel={() => setShowNew(false)} saveLabel="Crear disparador" />}

            {colDisps.length === 0 && !showNew && (
              <div style={{ textAlign:"center", padding:"48px 24px", color:"#94a3b8" }}>
                <p style={{ fontSize:32, margin:"0 0 12px" }}>⚡</p>
                <p style={{ fontSize:14, fontWeight:700, color:"#64748b", margin:"0 0 6px" }}>Sin disparadores</p>
                <p style={{ fontSize:12, margin:0 }}>Crea el primero para automatizar acciones en esta columna</p>
              </div>
            )}

            {colDisps.map(disp => (
              <div key={disp.id} style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:12, marginBottom:10, overflow:"hidden", opacity:disp.activo?1:0.6 }}>
                {editando?.id === disp.id ? (
                  <div style={{ padding:16 }}>
                    <DispForm key={`edit-${disp.id}`} initialData={editando} allCols={allCols} onSave={updateDisp} onCancel={() => setEditando(null)} saveLabel="Guardar cambios" />
                  </div>
                ) : (
                  <div style={{ padding:"14px 16px" }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:16 }}>{disp.activo?"⚡":"💤"}</span>
                        <span style={{ fontSize:13, fontWeight:700, color:"#1e293b" }}>{disp.nombre || "Sin nombre"}</span>
                        <span style={{ fontSize:10, padding:"1px 7px", borderRadius:10, background:disp.activo?"#dcfce7":"#f1f5f9", color:disp.activo?"#059669":"#64748b", fontWeight:700 }}>{disp.activo?"Activo":"Inactivo"}</span>
                      </div>
                      <div style={{ display:"flex", gap:6 }}>
                        <button onClick={() => toggleDisp(disp.id)} style={{ padding:"5px 10px", border:"1px solid #e2e8f0", background:"#f8fafc", borderRadius:7, fontSize:11, cursor:"pointer", color:"#475569", fontWeight:600 }}>{disp.activo?"Pausar":"Activar"}</button>
                        <button onClick={() => { setEditando({...disp, condicion:{...disp.condicion}, accion:{...disp.accion}}); setShowNew(false); }} style={{ padding:"5px 10px", border:"1px solid #e2e8f0", background:"#f8fafc", borderRadius:7, fontSize:11, cursor:"pointer", color:"#475569" }}>✏ Editar</button>
                        <select onChange={e => { if(e.target.value) { copyTo(disp, Number(e.target.value)); e.target.value=""; }}} style={{ padding:"5px 10px", border:"1px solid #e2e8f0", background:"#f8fafc", borderRadius:7, fontSize:11, cursor:"pointer", color:"#475569", outline:"none" }}>
                          <option value="">📋 Copiar a...</option>
                          {allCols.filter(c => c.id !== activeCol.id).map(c => <option key={c.id} value={c.id}>{c.agency?.replace("Rs Viajes ","") || ""} · {c.title}</option>)}
                        </select>
                        <button onClick={() => deleteDisp(disp.id)} style={{ padding:"5px 10px", border:"1px solid #fecaca", background:"#fef2f2", borderRadius:7, fontSize:11, cursor:"pointer", color:"#ef4444" }}>🗑</button>
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <div style={{ flex:1, background:"#f0f9ff", borderRadius:8, padding:"10px 12px", border:"1px solid #bae6fd" }}>
                        <p style={{ fontSize:10, fontWeight:700, color:"#0284c7", margin:"0 0 4px", textTransform:"uppercase" }}>⚡ Condición</p>
                        <p style={{ fontSize:12, color:"#1e293b", margin:0, fontWeight:600 }}>{CONDICION_TIPOS.find(t=>t.id===disp.condicion?.tipo)?.label || disp.condicion?.tipo}</p>
                        {disp.condicion?.horas && <p style={{ fontSize:11, color:"#64748b", margin:"3px 0 0" }}>{disp.condicion.horas} {disp.condicion.tipo==="tiempo_en_columna"?"días":"horas"}</p>}
                      </div>
                      <div style={{ display:"flex", alignItems:"center", color:"#94a3b8", fontSize:18 }}>→</div>
                      <div style={{ flex:1, background:"#f0fdf4", borderRadius:8, padding:"10px 12px", border:"1px solid #bbf7d0" }}>
                        <p style={{ fontSize:10, fontWeight:700, color:"#059669", margin:"0 0 4px", textTransform:"uppercase" }}>🎯 Acción</p>
                        <p style={{ fontSize:12, color:"#1e293b", margin:0, fontWeight:600 }}>{ACCION_TIPOS.find(t=>t.id===disp.accion?.tipo)?.label || disp.accion?.tipo}</p>
                        {disp.accion?.columna_destino && <p style={{ fontSize:11, color:"#64748b", margin:"3px 0 0" }}>→ {allCols.find(c=>c.id===disp.accion.columna_destino)?.title || disp.accion.columna_destino}</p>}
                        {disp.accion?.webhook_url && <p style={{ fontSize:10, color:"#64748b", margin:"3px 0 0", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{disp.accion.webhook_url}</p>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        ) : (
          <div style={{ textAlign:"center", padding:"48px", color:"#94a3b8" }}>
            <p style={{ fontSize:32, margin:"0 0 12px" }}>👈</p>
            <p style={{ fontSize:14, fontWeight:700, color:"#64748b", margin:0 }}>Selecciona una columna</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Componente para editar el webhook de n8n por agencia — estado local para evitar pérdida de foco
function WebhookN8nInput({ agencia, agenciaId }) {
  const [url, setUrl]       = useState("");
  const [saved, setSaved]   = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || "http://localhost:3001"}/agencias`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("crm_token")}` }
    }).then(r => r.json()).then(data => {
      const ag = data.find(a => a.id === agenciaId);
      setUrl(ag?.webhook_n8n || "");
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [agenciaId]);

  const handleSave = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:3001"}/agencias/${agenciaId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crm_token")}` },
        body: JSON.stringify({ webhook_n8n: url })
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { alert("Error guardando webhook"); }
  };

  if (loading) return <p style={{fontSize:12,color:"#94a3b8",margin:0}}>Cargando...</p>;

  return (
    <div style={{display:"flex",gap:8}}>
      <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://tu-n8n.com/webhook/agencia-periodismo" style={{flex:1,padding:"9px 12px",border:"1px solid #bbf7d0",borderRadius:8,fontSize:12,outline:"none",background:"#fff",boxSizing:"border-box"}} />
      <button onClick={handleSave} style={{padding:"9px 16px",background:saved?"#059669":"#10b981",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",transition:"background 0.2s",flexShrink:0}}>{saved?"✓ Guardado":"Guardar"}</button>
    </div>
  );
}

// ─── TARJETAS SECTION ─────────────────────────────────────────────────────────
// Configura qué campos aparecen en las tarjetas del Kanban

const CARD_FIELDS_KEY = "crm_card_fields";
const DEFAULT_CARD_FIELDS = [2004104, 2004106, 2004107]; // Destino, Fechas, Adultos

function loadCardFields() {
  try { return JSON.parse(localStorage.getItem(CARD_FIELDS_KEY)) || DEFAULT_CARD_FIELDS; }
  catch { return DEFAULT_CARD_FIELDS; }
}
function saveCardFields(ids) {
  localStorage.setItem(CARD_FIELDS_KEY, JSON.stringify(ids));
}

// Export hook para que KanbanView pueda leerlo
function useCardFields() {
  const [cardFields, setCardFields] = useState(loadCardFields);
  const update = (ids) => { setCardFields(ids); saveCardFields(ids); };
  return [cardFields, update];
}

function TarjetasSection({ cfgFields }) {
  const [cardFields, setCardFieldsLocal] = useState(loadCardFields);

  const toggle = (id) => {
    const next = cardFields.includes(id) ? cardFields.filter(f=>f!==id) : [...cardFields, id];
    setCardFieldsLocal(next);
    saveCardFields(next);
  };

  const moveUp = (id) => {
    const idx = cardFields.indexOf(id);
    if (idx <= 0) return;
    const next = [...cardFields];
    [next[idx-1], next[idx]] = [next[idx], next[idx-1]];
    setCardFieldsLocal(next); saveCardFields(next);
  };
  const moveDown = (id) => {
    const idx = cardFields.indexOf(id);
    if (idx >= cardFields.length-1) return;
    const next = [...cardFields];
    [next[idx], next[idx+1]] = [next[idx+1], next[idx]];
    setCardFieldsLocal(next); saveCardFields(next);
  };

  return (
    <div>
      <p style={{fontSize:13,color:T.textSub,margin:"0 0 20px",lineHeight:1.6}}>
        Elige qué campos se muestran en las tarjetas del tablero Kanban. Todas las tarjetas muestran siempre el nombre del cliente — aquí configuras los campos adicionales.
      </p>

      {/* Preview tarjeta */}
      <div style={{marginBottom:24}}>
        <p style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:0.5,margin:"0 0 8px"}}>Vista previa</p>
        <div style={{width:220,background:"#fff",borderRadius:12,padding:"12px 14px",border:`1px solid ${T.border}`,boxShadow:"0 2px 8px rgba(0,0,0,0.07)"}}>
          <div style={{display:"flex",gap:6,marginBottom:6}}>
            <span style={{fontSize:9,color:T.textMuted,fontWeight:600}}>#1234</span>
            <span style={{fontSize:9,color:T.textMuted}}>14:30</span>
          </div>
          <p style={{margin:"0 0 6px",fontSize:13,fontWeight:700,color:T.textPrimary}}>María García</p>
          <p style={{margin:"0 0 6px",fontSize:11,color:T.textSub}}>+52 555 000 0000</p>
          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
            {cardFields.map(id => {
              const f = cfgFields.find(fi=>fi.id===id);
              return f ? <span key={id} style={{fontSize:9,padding:"2px 6px",borderRadius:8,background:T.primaryLt,color:T.primary,fontWeight:600}}>{f.label}</span> : null;
            })}
          </div>
        </div>
      </div>

      {/* Campos seleccionados */}
      <p style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:0.5,margin:"0 0 8px"}}>Campos seleccionados ({cardFields.length})</p>
      <div style={{background:"#fff",border:`1px solid ${T.border}`,borderRadius:14,overflow:"hidden",marginBottom:20}}>
        {cardFields.length === 0 && <div style={{padding:16,textAlign:"center",fontSize:13,color:T.textMuted}}>Ningún campo seleccionado</div>}
        {cardFields.map((id, idx) => {
          const f = cfgFields.find(fi=>fi.id===id);
          if (!f) return null;
          return (
            <div key={id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderBottom:idx<cardFields.length-1?`1px solid ${T.panel}`:"none"}}>
              <span style={{fontSize:13,color:T.textPrimary,fontWeight:600,flex:1}}>{f.label}</span>
              <code style={{fontSize:11,color:T.primary,background:T.primaryLt,padding:"1px 6px",borderRadius:5}}>{f.id}</code>
              <div style={{display:"flex",gap:3}}>
                <button onClick={()=>moveUp(id)} style={{padding:"3px 7px",border:`1px solid ${T.border}`,borderRadius:5,background:"#fff",cursor:"pointer",fontSize:11,color:T.textSub}}>↑</button>
                <button onClick={()=>moveDown(id)} style={{padding:"3px 7px",border:`1px solid ${T.border}`,borderRadius:5,background:"#fff",cursor:"pointer",fontSize:11,color:T.textSub}}>↓</button>
                <button onClick={()=>toggle(id)} style={{padding:"3px 7px",border:"1px solid #fecaca",borderRadius:5,background:"#fef2f2",cursor:"pointer",fontSize:11,color:"#dc2626",fontWeight:700}}>×</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Campos disponibles */}
      <p style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:0.5,margin:"0 0 8px"}}>Campos disponibles</p>
      <div style={{background:"#fff",border:`1px solid ${T.border}`,borderRadius:14,overflow:"hidden"}}>
        {cfgFields.filter(f=>!cardFields.includes(f.id)).map((f,i,arr) => (
          <div key={f.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderBottom:i<arr.length-1?`1px solid ${T.panel}`:"none"}}>
            <span style={{fontSize:13,color:T.textSub,flex:1}}>{f.label}</span>
            <code style={{fontSize:11,color:T.textMuted,background:T.panel,padding:"1px 6px",borderRadius:5}}>{f.id}</code>
            <button onClick={()=>toggle(f.id)} style={{padding:"4px 12px",background:T.primaryLt,border:`1px solid ${T.primary}22`,borderRadius:8,color:T.primary,fontSize:11,fontWeight:700,cursor:"pointer"}}>+ Añadir</button>
          </div>
        ))}
        {cfgFields.filter(f=>!cardFields.includes(f.id)).length === 0 && (
          <div style={{padding:16,textAlign:"center",fontSize:13,color:T.textMuted}}>Todos los campos ya están en la tarjeta</div>
        )}
      </div>
    </div>
  );
}

// ─── IDENTIFICADORES SECTION ──────────────────────────────────────────────────

function IdentificadoresSection({ cfgFields, setCfgFields, cfgColumns }) {
  const [showNewField, setShowNewField] = useState(false);
  const [newField, setNewField] = useState({ label:"", tipo:"text", visible_agentes: true });
  const [saving, setSaving] = useState(false);

  const handleCreateField = async () => {
    if (!newField.label.trim()) return;
    setSaving(true);
    try {
      const created = await campos.crear({
        label: newField.label,
        tipo: newField.tipo,
        visible_agentes: newField.visible_agentes,
      });
      setCfgFields(prev => [...prev, {
        id: created.id,
        label: created.label || newField.label,
        type: created.tipo || newField.tipo,
        visible_agentes: created.visible_agentes ?? newField.visible_agentes,
      }]);
      setNewField({ label:"", tipo:"text", visible_agentes: true });
      setShowNewField(false);
    } catch(err) { alert("Error: " + err.message); }
    finally { setSaving(false); }
  };

  const handleToggleVisible = async (field) => {
    try {
      // Optimistic update
      setCfgFields(prev => prev.map(f => f.id===field.id ? {...f, visible_agentes:!f.visible_agentes} : f));
      // Si la API soporta PATCH /campos/:id
      // await campos.actualizar(field.id, { visible_agentes: !field.visible_agentes });
    } catch(err) { alert("Error: " + err.message); }
  };

  const handleDeleteField = async (field) => {
    if (!window.confirm(`¿Eliminar el campo "${field.label}"?`)) return;
    try {
      await campos.eliminar(field.id);
      setCfgFields(prev => prev.filter(f => f.id !== field.id));
    } catch(err) { alert("Error: " + err.message); }
  };

  const inputStyle = { width:"100%", padding:"10px 12px", border:`1px solid ${T.border}`, borderRadius:10, fontSize:13, outline:"none", boxSizing:"border-box", fontFamily:"inherit", background:"#fff" };
  const labelStyle = { fontSize:11, fontWeight:700, color:T.textSub, display:"block", marginBottom:6, letterSpacing:0.4 };

  return (
    <div>
      {/* Ejemplo n8n */}
      <div style={{background:"#1c1917",borderRadius:12,padding:"14px 16px",marginBottom:24,fontFamily:"monospace"}}>
        <p style={{fontSize:10,color:"#a8a29e",margin:"0 0 8px",fontWeight:700}}>EJEMPLO N8N</p>
        <code style={{fontSize:11,color:"#86efac",display:"block",lineHeight:2,whiteSpace:"pre"}}>{`{ "campos": [\n    { "campo_id": 2004104, "valor": "Ixtapa" }\n  ] }`}</code>
      </div>

      {/* Header campos */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <h4 style={{margin:0,fontSize:12,fontWeight:800,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5}}>Campos del cliente</h4>
        <button onClick={()=>setShowNewField(true)} style={{padding:"7px 14px",background:T.primary,color:"#fff",border:"none",borderRadius:10,fontSize:11,fontWeight:700,cursor:"pointer"}}>+ Nuevo campo</button>
      </div>

      {/* Modal nuevo campo */}
      {showNewField && (
        <CfgModal onClose={()=>setShowNewField(false)}>
          <h3 style={{margin:"0 0 20px",fontSize:16,fontWeight:800,color:T.textPrimary}}>Nuevo campo</h3>
          <div style={{marginBottom:14}}>
            <label style={labelStyle}>NOMBRE DEL CAMPO</label>
            <input value={newField.label} onChange={e=>setNewField(p=>({...p,label:e.target.value}))} placeholder="Ej: Destino, Fechas de viaje..." style={inputStyle} autoFocus />
          </div>
          <div style={{marginBottom:14}}>
            <label style={labelStyle}>TIPO</label>
            <select value={newField.tipo} onChange={e=>setNewField(p=>({...p,tipo:e.target.value}))} style={{...inputStyle}}>
              <option value="text">Texto</option>
              <option value="number">Número</option>
              <option value="date">Fecha</option>
              <option value="select">Selección</option>
            </select>
          </div>
          <div style={{marginBottom:24,padding:"12px 14px",background:T.panel,borderRadius:10,border:`1px solid ${T.border}`}}>
            <CfgToggle value={newField.visible_agentes} onChange={v=>setNewField(p=>({...p,visible_agentes:v}))} label="Visible y editable para agentes" />
            <p style={{margin:"6px 0 0 44px",fontSize:11,color:T.textMuted}}>Si está desactivado, solo el admin lo ve. Útil para campos que actualiza n8n.</p>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={handleCreateField} disabled={saving} style={{flex:1,padding:"10px",background:T.primary,color:"#fff",border:"none",borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer"}}>
              {saving ? "Guardando..." : "Crear campo"}
            </button>
            <button onClick={()=>setShowNewField(false)} style={{flex:1,padding:"10px",background:T.panel,color:T.textSub,border:"none",borderRadius:10,fontSize:13,cursor:"pointer"}}>Cancelar</button>
          </div>
        </CfgModal>
      )}

      {/* Tabla campos */}
      <div style={{background:"#fff",border:`1px solid ${T.border}`,borderRadius:14,overflow:"hidden",marginBottom:28}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 100px 70px 120px auto",padding:"8px 16px",background:T.panel,borderBottom:`1px solid ${T.border}`,gap:8}}>
          {["CAMPO","ID","TIPO","VISIBLE AGENTES",""].map((h,i)=><span key={i} style={{fontSize:10,fontWeight:700,color:T.textSub,letterSpacing:0.4}}>{h}</span>)}
        </div>
        {cfgFields.length === 0 && (
          <div style={{padding:"24px",textAlign:"center",color:T.textMuted,fontSize:13}}>No hay campos creados</div>
        )}
        {cfgFields.map((f,i)=>(
          <div key={f.id} style={{display:"grid",gridTemplateColumns:"1fr 100px 70px 120px auto",padding:"11px 16px",borderBottom:i<cfgFields.length-1?`1px solid ${T.panel}`:"none",alignItems:"center",gap:8}}>
            <span style={{fontSize:13,color:T.textPrimary,fontWeight:600}}>{f.label}</span>
            <code style={{fontSize:11,color:T.primary,background:T.primaryLt,padding:"2px 7px",borderRadius:6}}>{f.id}</code>
            <span style={{fontSize:11,color:T.textSub,textTransform:"capitalize"}}>{f.type||f.tipo||"text"}</span>
            <div style={{display:"flex",justifyContent:"center"}}>
              <CfgToggle value={f.visible_agentes !== false} onChange={()=>handleToggleVisible(f)} />
            </div>
            <div style={{display:"flex",gap:6}}>
              <CopyBtn text={f.id} />
              <button onClick={()=>handleDeleteField(f)} style={{fontSize:11,padding:"2px 7px",borderRadius:6,border:"1px solid #fecaca",background:"#fef2f2",color:"#dc2626",cursor:"pointer",fontWeight:600}}>×</button>
            </div>
          </div>
        ))}
      </div>

      {/* IDs columnas */}
      <h4 style={{margin:"0 0 10px",fontSize:12,fontWeight:800,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5}}>IDs de columnas</h4>
      <div style={{background:"#fff",border:`1px solid ${T.border}`,borderRadius:14,overflow:"hidden"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 100px 1fr auto",padding:"8px 16px",background:T.panel,borderBottom:`1px solid ${T.border}`,gap:8}}>
          {["COLUMNA","ID","AGENCIA",""].map((h,i)=><span key={i} style={{fontSize:10,fontWeight:700,color:T.textSub,letterSpacing:0.4}}>{h}</span>)}
        </div>
        {cfgColumns.map((c,i)=>(
          <div key={c.id} style={{display:"grid",gridTemplateColumns:"1fr 100px 1fr auto",padding:"10px 16px",borderBottom:i<cfgColumns.length-1?`1px solid ${T.panel}`:"none",alignItems:"center",gap:8}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}><div style={{width:10,height:10,borderRadius:"50%",background:c.color,flexShrink:0}} /><span style={{fontSize:13,color:T.textPrimary,fontWeight:600}}>{c.title}</span></div>
            <code style={{fontSize:11,color:T.primary,background:T.primaryLt,padding:"2px 7px",borderRadius:6}}>{c.id}</code>
            <span style={{fontSize:11,color:T.textSub}}>{c.agency?.replace("Rs Viajes ","")}</span>
            <CopyBtn text={c.id} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ConfigPanel({ userRole, userId }) {
  const [activeSection, setActiveSection] = useState("cuentas");
  const [cfgUsers,    setCfgUsers]    = useState([]);
  const [cfgColumns,  setCfgColumns]  = useState([]);
  const [cfgFields,   setCfgFields]   = useState([]);
  const [cfgSources,  setCfgSources]  = useState([]);
  const [statPerms,   setStatPerms]   = useState(INIT_STAT_PERMS);
  const [loading,     setLoading]     = useState(true);

  // Cargar datos reales de la API al montar
  useEffect(() => {
    const cargar = async () => {
      try {
        setLoading(true);
        const [u, c, f, s] = await Promise.all([
          usuarios.listar(),
          columnas.listar(),
          campos.listar(),
          fuentes.listar(),
        ]);
        setCfgUsers(u.map(usr => ({
          id: usr.id,
          name: usr.nombre,
          email: usr.email,
          role: usr.rol,
          agency: usr.agencia || null,
          active: usr.activo,
          columns: []
        })));
        setCfgColumns(c.map(col => ({
          id: col.id,
          title: col.titulo,
          color: col.color || "#6366f1",
          agency: col.agencia || "",
          agencia_id: col.agencia_id,
          order: col.orden || 0,
          type: col.tipo || "generic",
          assignedTo: col.agente_id || null,
          visibleToAll: col.visible_todos || false
        })));
        setCfgFields(f.map(fi => ({ id: fi.id, label: fi.label || fi.nombre, type: fi.tipo || fi.type || "text" })));
        setCfgSources(s.map(src => ({
          id: src.id,
          agency: src.agencia || src.agency || "",
          platform: src.plataforma || src.platform || "",
          name: src.nombre || src.name || "",
          connected: src.conectado ?? src.connected ?? false
        })));
      } catch (err) {
        console.error("Error cargando configuración:", err);
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, []);

  const ROLE_CFG = { admin:{label:"Admin",color:"#f59e0b",icon:"⚙"}, manager:{label:"Manager",color:"#ef4444",icon:"🎯"}, supervisor:{label:"Supervisor",color:"#8b5cf6",icon:"👁"}, agente:{label:"Agente",color:"#3b82f6",icon:"💼"} };

  const allowedSections = CONFIG_SECTIONS.filter(s => SECTION_ACCESS[s.id].includes(userRole));
  const section = allowedSections.find(s => s.id===activeSection) || allowedSections[0];
  const activeId = section?.id;

  const agentesOf = (agency) => cfgUsers.filter(u => u.role==="agente" && u.agency===agency && u.active);
  const allowedRoles = userRole==="admin" ? ["admin","manager","supervisor","agente"] : ["supervisor","agente"];

  // ── Cuentas ──
  const [showNewUser, setShowNewUser] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const emptyUser = {name:"",email:"",password:"",role:"agente",agency:AGENCIES_LIST[0],columns:[],active:true};
  const [newUser, setNewUser] = useState(emptyUser);

  const toggleUserCol = (setter, colId) => {
    setter(p => ({...p, columns: p.columns.includes(colId) ? p.columns.filter(c=>c!==colId) : [...p.columns, colId]}));
  };

  // ── Columnas ──
  const [cfgActiveAgency, setCfgActiveAgency] = useState(AGENCIES_LIST[0]);
  const [showNewCol, setShowNewCol] = useState(false);
  const [editCol, setEditCol] = useState(null);
  const [newCol, setNewCol] = useState({title:"",color:"#6366f1",type:"generic",assignedTo:null});
  const dragItem = {current:null}; const dragOver = {current:null};
  const agencyCols = cfgColumns.filter(c=>c.agency===cfgActiveAgency).sort((a,b)=>a.order-b.order);

  const handleDragEnd = () => {
    const items=[...agencyCols];
    const dragged=items.splice(dragItem.current,1)[0];
    items.splice(dragOver.current,0,dragged);
    const updated=items.map((col,i)=>({...col,order:i}));
    setCfgColumns(prev=>prev.map(c=>{const u=updated.find(u=>u.id===c.id);return u||c;}));
  };

  if (loading) return (
    <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",background:"#f1f5f9",flexDirection:"column",gap:12}}>
      <div style={{width:36,height:36,border:"3px solid #e2e8f0",borderTop:"3px solid #6366f1",borderRadius:"50%",animation:"spin 0.8s linear infinite"}} />
      <p style={{fontSize:13,color:"#94a3b8",margin:0}}>Cargando configuración...</p>
    </div>
  );

  // Modal nueva cuenta — estado independiente para evitar pérdida de foco
  const NewUserModal = () => {
    const [localUser, setLocalUser] = useState({name:"",email:"",password:"",role:"agente",agency:AGENCIES_LIST[0],columns:[],active:true});
    const agCols = localUser.role==="agente" ? cfgColumns.filter(c=>c.agency===localUser.agency).sort((a,b)=>a.order-b.order) : [];
    return (
      <CfgModal onClose={()=>setShowNewUser(false)}>
        <h3 style={{margin:"0 0 20px",fontSize:16,fontWeight:800,color:"#1e293b"}}>Nueva cuenta</h3>
        <div style={{marginBottom:14}}>
          <label style={{fontSize:11,fontWeight:700,color:"#64748b",display:"block",marginBottom:6}}>NOMBRE</label>
          <input value={localUser.name} onChange={e=>setLocalUser(p=>({...p,name:e.target.value}))} placeholder="Ej: Laura Santos" style={{width:"100%",padding:"10px 12px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}} />
        </div>
        <div style={{marginBottom:14}}>
          <label style={{fontSize:11,fontWeight:700,color:"#64748b",display:"block",marginBottom:6}}>EMAIL</label>
          <input value={localUser.email} onChange={e=>setLocalUser(p=>({...p,email:e.target.value}))} placeholder="laura@rsviajes.com" style={{width:"100%",padding:"10px 12px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}} />
        </div>
        <div style={{marginBottom:14}}>
          <label style={{fontSize:11,fontWeight:700,color:"#64748b",display:"block",marginBottom:6}}>CONTRASEÑA</label>
          <input type="password" value={localUser.password} onChange={e=>setLocalUser(p=>({...p,password:e.target.value}))} placeholder="••••••••" style={{width:"100%",padding:"10px 12px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}} />
        </div>
        <div style={{marginBottom:14}}>
          <label style={{fontSize:11,fontWeight:700,color:"#64748b",display:"block",marginBottom:6}}>ROL</label>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {allowedRoles.map(key=>(
              <button key={key} onClick={()=>setLocalUser(p=>({...p,role:key,columns:[]}))} style={{flex:1,minWidth:80,padding:"8px",border:"1px solid",borderColor:localUser.role===key?ROLE_CFG[key].color:"#e2e8f0",background:localUser.role===key?ROLE_CFG[key].color+"22":"#fff",color:localUser.role===key?ROLE_CFG[key].color:"#64748b",borderRadius:8,fontSize:12,cursor:"pointer",fontWeight:600}}>
                {ROLE_CFG[key].icon} {ROLE_CFG[key].label}
              </button>
            ))}
          </div>
        </div>
        {localUser.role==="agente" && <>
          <div style={{marginBottom:14}}>
            <label style={{fontSize:11,fontWeight:700,color:"#64748b",display:"block",marginBottom:6}}>AGENCIA</label>
            <select value={localUser.agency} onChange={e=>setLocalUser(p=>({...p,agency:e.target.value,columns:[]}))} style={{width:"100%",padding:"10px 12px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:13,outline:"none"}}>
              {AGENCIES_LIST.map(a=><option key={a}>{a}</option>)}
            </select>
          </div>
          <div style={{marginBottom:20}}>
            <label style={{fontSize:11,fontWeight:700,color:"#64748b",display:"block",marginBottom:8}}>COLUMNAS</label>
            <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:200,overflowY:"auto"}}>
              {agCols.map(col=>(
                <label key={col.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"#f8fafc",borderRadius:8,cursor:"pointer",border:localUser.columns.includes(col.id)?"1px solid #6366f1":"1px solid #e2e8f0"}}>
                  <input type="checkbox" checked={localUser.columns.includes(col.id)} onChange={()=>setLocalUser(p=>({...p,columns:p.columns.includes(col.id)?p.columns.filter(c=>c!==col.id):[...p.columns,col.id]}))} style={{accentColor:"#6366f1"}} />
                  <div style={{width:10,height:10,borderRadius:"50%",background:col.color,flexShrink:0}} />
                  <span style={{fontSize:12,color:"#1e293b",flex:1}}>{col.title}</span>
                </label>
              ))}
            </div>
          </div>
        </>}
        <div style={{display:"flex",gap:8}}>
          <button onClick={async ()=>{
            if(!localUser.name||!localUser.email) return;
            try {
              const nuevo = await usuarios.crear({ nombre: localUser.name, email: localUser.email, password: localUser.password, rol: localUser.role, agencia_id: localUser.role==="agente" ? (AGENCY_IDS[localUser.agency] || null) : null });
              if (localUser.columns.length > 0) await usuarios.setColumnas(nuevo.id, localUser.columns);
              setCfgUsers(prev=>[...prev,{id:nuevo.id,name:nuevo.nombre||localUser.name,email:nuevo.email,role:nuevo.rol||localUser.role,agency:localUser.agency,active:true,columns:localUser.columns}]);
              setShowNewUser(false);
            } catch(err){ alert("Error: "+err.message); }
          }} style={{flex:1,padding:"10px",background:"#6366f1",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer"}}>Crear cuenta</button>
          <button onClick={()=>setShowNewUser(false)} style={{flex:1,padding:"10px",background:"#f1f5f9",color:"#475569",border:"none",borderRadius:8,fontSize:13,cursor:"pointer"}}>Cancelar</button>
        </div>
      </CfgModal>
    );
  };

  // Modal editar cuenta — con estado local para evitar pérdida de foco
  const EditUserModal = () => {
    const [localUser, setLocalUser] = useState({...editUser});
    if (!editUser) return null;
    const agCols = localUser.role==="agente" ? cfgColumns.filter(c=>c.agency===localUser.agency).sort((a,b)=>a.order-b.order) : [];
    const toggleCol = (colId) => setLocalUser(p => ({...p, columns: p.columns.includes(colId) ? p.columns.filter(c=>c!==colId) : [...p.columns, colId]}));
    return (
      <CfgModal onClose={()=>setEditUser(null)}>
        <h3 style={{margin:"0 0 20px",fontSize:16,fontWeight:800,color:"#1e293b"}}>Editar cuenta</h3>
        <div style={{marginBottom:14}}>
          <label style={{fontSize:11,fontWeight:700,color:"#64748b",display:"block",marginBottom:6}}>NOMBRE</label>
          <input value={localUser.name} onChange={e=>setLocalUser(p=>({...p,name:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}} />
        </div>
        <div style={{marginBottom:14}}>
          <label style={{fontSize:11,fontWeight:700,color:"#64748b",display:"block",marginBottom:6}}>EMAIL</label>
          <input value={localUser.email} onChange={e=>setLocalUser(p=>({...p,email:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}} />
        </div>
        <div style={{marginBottom:14}}>
          <label style={{fontSize:11,fontWeight:700,color:"#64748b",display:"block",marginBottom:6}}>CONTRASEÑA (dejar vacío para no cambiar)</label>
          <input type="password" value={localUser.password||""} onChange={e=>setLocalUser(p=>({...p,password:e.target.value}))} placeholder="••••••••" style={{width:"100%",padding:"10px 12px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}} />
        </div>
        <div style={{marginBottom:14}}>
          <label style={{fontSize:11,fontWeight:700,color:"#64748b",display:"block",marginBottom:6}}>ROL</label>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {allowedRoles.map(key=>(
              <button key={key} onClick={()=>setLocalUser(p=>({...p,role:key,columns:[]}))} style={{flex:1,minWidth:80,padding:"8px",border:"1px solid",borderColor:localUser.role===key?ROLE_CFG[key].color:"#e2e8f0",background:localUser.role===key?ROLE_CFG[key].color+"22":"#fff",color:localUser.role===key?ROLE_CFG[key].color:"#64748b",borderRadius:8,fontSize:12,cursor:"pointer",fontWeight:600}}>
                {ROLE_CFG[key].icon} {ROLE_CFG[key].label}
              </button>
            ))}
          </div>
        </div>
        {localUser.role==="agente" && <>
          <div style={{marginBottom:14}}>
            <label style={{fontSize:11,fontWeight:700,color:"#64748b",display:"block",marginBottom:6}}>AGENCIA</label>
            <select value={localUser.agency||""} onChange={e=>setLocalUser(p=>({...p,agency:e.target.value,columns:[]}))} style={{width:"100%",padding:"10px 12px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:13,outline:"none"}}>
              {AGENCIES_LIST.map(a=><option key={a}>{a}</option>)}
            </select>
          </div>
          <div style={{marginBottom:20}}>
            <label style={{fontSize:11,fontWeight:700,color:"#64748b",display:"block",marginBottom:8}}>COLUMNAS</label>
            <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:200,overflowY:"auto"}}>
              {agCols.map(col=>(
                <label key={col.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"#f8fafc",borderRadius:8,cursor:"pointer",border:localUser.columns.includes(col.id)?"1px solid #6366f1":"1px solid #e2e8f0"}}>
                  <input type="checkbox" checked={localUser.columns.includes(col.id)} onChange={()=>toggleCol(col.id)} style={{accentColor:"#6366f1"}} />
                  <div style={{width:10,height:10,borderRadius:"50%",background:col.color,flexShrink:0}} />
                  <span style={{fontSize:12,color:"#1e293b",flex:1}}>{col.title}</span>
                </label>
              ))}
            </div>
          </div>
        </>}
        <div style={{display:"flex",gap:8,marginBottom:8}}>
          <button onClick={async ()=>{
            try {
              await usuarios.actualizar(localUser.id, { nombre: localUser.name, email: localUser.email, rol: localUser.role, activo: localUser.active, ...(localUser.password ? {password: localUser.password} : {}) });
              if (localUser.role === "agente") await usuarios.setColumnas(localUser.id, localUser.columns);
              setCfgUsers(prev=>prev.map(u=>u.id===localUser.id?localUser:u));
              setEditUser(null);
            } catch(err){ alert("Error: "+err.message); }
          }} style={{flex:1,padding:"10px",background:"#6366f1",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer"}}>Guardar</button>
          <button onClick={()=>setEditUser(null)} style={{flex:1,padding:"10px",background:"#f1f5f9",color:"#475569",border:"none",borderRadius:8,fontSize:13,cursor:"pointer"}}>Cancelar</button>
        </div>
        {(() => { const ROLE_RANK = {admin:4,manager:3,supervisor:2,agente:1}; const canDel = userRole==="admin" || ROLE_RANK[userRole] > ROLE_RANK[localUser.role]; return canDel && !(userRole!=="admin" && localUser.role==="admin"); })() && (
          <button onClick={async ()=>{
            if(!window.confirm(`¿Eliminar la cuenta de ${localUser.name}? Esta acción no se puede deshacer.`)) return;
            try {
              await usuarios.eliminar(localUser.id);
              setCfgUsers(prev=>prev.filter(u=>u.id!==localUser.id));
              setEditUser(null);
            } catch(err){ alert("Error al eliminar: "+err.message); }
          }} style={{width:"100%",padding:"10px",background:"#fef2f2",color:"#ef4444",border:"1px solid #fecaca",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer"}}>
            🗑 Eliminar cuenta
          </button>
        )}
      </CfgModal>
    );
  };

  const renderSection = () => {
    switch(activeId) {

      // ── CUENTAS ──
      case "cuentas": return (
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
            <h3 style={{margin:0,fontSize:15,fontWeight:800,color:"#1e293b"}}>Cuentas de usuario</h3>
            <button onClick={()=>setShowNewUser(true)} style={{padding:"8px 16px",background:"#6366f1",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>+ Nueva cuenta</button>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {cfgUsers.map((u)=>{
              const ROLE_RANK = { admin: 4, manager: 3, supervisor: 2, agente: 1 };
              const isSelf = u.id === userId;
              const canControl = userRole === "admin" || isSelf || ROLE_RANK[userRole] > ROLE_RANK[u.role];
              const canDeactivate = canControl && !isSelf;
              return (
              <div key={u.id} style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,padding:"14px 16px",display:"flex",alignItems:"center",gap:12,opacity:u.active?1:0.55}}>
                <div style={{width:40,height:40,borderRadius:"50%",background:ROLE_CFG[u.role].color+"22",border:`2px solid ${ROLE_CFG[u.role].color}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{ROLE_CFG[u.role].icon}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3,flexWrap:"wrap"}}>
                    <span style={{fontSize:13,fontWeight:700,color:"#1e293b"}}>{u.name}</span>
                    <CfgBadge label={ROLE_CFG[u.role].label} color={ROLE_CFG[u.role].color} />
                    {!u.active && <CfgBadge label="Inactivo" color="#64748b" />}
                  </div>
                  <span style={{fontSize:11,color:"#64748b"}}>{u.email}</span>
                  {u.agency && <span style={{fontSize:11,color:"#94a3b8",marginLeft:8}}>· {u.agency.replace("Rs Viajes ","")}</span>}
                </div>
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  {canControl ? (
                    <>
                      <button onClick={()=>setEditUser({...u})} style={{padding:"6px 12px",background:"#f1f5f9",border:"none",borderRadius:8,fontSize:12,color:"#475569",cursor:"pointer",fontWeight:600}}>Editar</button>
                      {canDeactivate && <button onClick={async ()=>{ try { await usuarios.actualizar(u.id,{activo:!u.active}); setCfgUsers(prev=>prev.map(usr=>usr.id===u.id?{...usr,active:!usr.active}:usr)); } catch(err){ alert("Error: "+err.message); }}} style={{padding:"6px 12px",background:u.active?"#fef2f2":"#f0fdf4",border:"none",borderRadius:8,fontSize:12,color:u.active?"#ef4444":"#059669",cursor:"pointer",fontWeight:600}}>{u.active?"Desactivar":"Activar"}</button>}
                    </>
                  ) : (
                    <span style={{fontSize:11,color:"#94a3b8",padding:"6px 12px"}}>Sin permisos</span>
                  )}
                </div>
              </div>
              );
            })}
          </div>
          {showNewUser && <NewUserModal />}
          {editUser && <EditUserModal />}
        </div>
      );

      // ── COLUMNAS ──
      case "columnas": return (
        <div>
          <div style={{display:"flex",marginBottom:24,borderBottom:"1px solid #e2e8f0"}}>
            {AGENCIES_LIST.map(a=><button key={a} onClick={()=>setCfgActiveAgency(a)} style={{padding:"8px 18px",border:"none",background:"transparent",fontSize:13,fontWeight:cfgActiveAgency===a?700:500,color:cfgActiveAgency===a?"#6366f1":"#64748b",borderBottom:cfgActiveAgency===a?"2px solid #6366f1":"2px solid transparent",cursor:"pointer"}}>{a.replace("Rs Viajes ","")}</button>)}
          </div>
          <p style={{fontSize:12,color:"#94a3b8",margin:"0 0 16px"}}>↔ Arrastra para reordenar</p>
          <div style={{display:"flex",gap:10,overflowX:"auto",paddingBottom:16,alignItems:"flex-start"}}>
            {agencyCols.map((col,index)=>{
              const agent=cfgUsers.find(u=>u.id===col.assignedTo);
              return (
                <div key={col.id} draggable onDragStart={()=>{dragItem.current=index;}} onDragEnter={()=>{dragOver.current=index;}} onDragEnd={handleDragEnd} onDragOver={e=>e.preventDefault()} style={{width:200,flexShrink:0,border:"1px solid #e2e8f0",borderRadius:12,overflow:"hidden",background:"#fff",boxShadow:"0 1px 4px rgba(0,0,0,0.06)",cursor:"grab"}}>
                  <div style={{height:5,background:col.color}} />
                  <div style={{padding:"12px 12px 14px"}}>
                    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:8,gap:6}}>
                      <div style={{flex:1}}>
                        <p style={{fontSize:12,fontWeight:800,color:"#1e293b",margin:"0 0 4px"}}>{col.title}</p>
                        <code style={{fontSize:10,color:"#6366f1",background:"#eef2ff",padding:"2px 6px",borderRadius:4}}>{col.id}</code>
                      </div>
                      <div style={{display:"flex",gap:3,flexShrink:0}}>
                        <button onClick={()=>setEditCol({...col})} style={{background:"#f1f5f9",border:"none",borderRadius:6,padding:"4px 7px",cursor:"pointer",fontSize:11,color:"#475569"}}>✏</button>
                        <button onClick={async ()=>{ try { await columnas.eliminar(col.id); setCfgColumns(prev=>prev.filter(c=>c.id!==col.id)); } catch(err){ alert("Error: "+err.message); }}} style={{background:"#fef2f2",border:"none",borderRadius:6,padding:"4px 7px",cursor:"pointer",fontSize:11,color:"#ef4444"}}>×</button>
                      </div>
                    </div>
                    <div style={{marginBottom:8}}>
                      {col.type==="generic" ? <CfgBadge label="Genérica" color="#10b981" /> : <CfgBadge label={`Asignada${agent?`: ${agent.name.split(" ")[0]}`:""}`} color="#f59e0b" />}
                    </div>
                    {col.type==="assigned" && <CfgToggle value={col.visibleToAll} onChange={async ()=>{ try { await columnas.visibilidad(col.id, !col.visibleToAll); setCfgColumns(prev=>prev.map(c=>c.id===col.id?{...c,visibleToAll:!c.visibleToAll}:c)); } catch(err){ alert("Error: "+err.message); }}} label="Visible para el equipo" />}
                    <CfgToggle value={col.stopAI||false} onChange={()=>setCfgColumns(prev=>prev.map(c=>c.id===col.id?{...c,stopAI:!c.stopAI}:c))} label="🤖 Stop AI (no enviar a n8n)" />
                  </div>
                </div>
              );
            })}
            {!showNewCol ? (
              <button onClick={()=>setShowNewCol(true)} style={{width:200,flexShrink:0,height:120,border:"2px dashed #e2e8f0",borderRadius:12,background:"transparent",color:"#94a3b8",fontSize:13,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:6}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor="#6366f1";e.currentTarget.style.color="#6366f1";e.currentTarget.style.background="#eef2ff";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor="#e2e8f0";e.currentTarget.style.color="#94a3b8";e.currentTarget.style.background="transparent";}}
              ><span style={{fontSize:24}}>+</span><span style={{fontWeight:600}}>Nueva columna</span></button>
            ) : (
              <div style={{width:220,flexShrink:0,border:"1px solid #6366f1",borderRadius:12,overflow:"hidden",background:"#fff"}}>
                <div style={{height:5,background:newCol.color}} />
                <div style={{padding:12}}>
                  <input autoFocus placeholder="Nombre" value={newCol.title} onChange={e=>setNewCol(p=>({...p,title:e.target.value}))} onKeyDown={e=>{if(e.key==="Enter"&&newCol.title.trim()){const id=Math.max(...cfgColumns.map(c=>c.id))+1;setCfgColumns(prev=>[...prev,{id,title:newCol.title,color:newCol.color,agency:cfgActiveAgency,order:agencyCols.length,type:newCol.type,assignedTo:newCol.type==="assigned"?newCol.assignedTo:null,visibleToAll:false}]);setNewCol({title:"",color:"#6366f1",type:"generic",assignedTo:null});setShowNewCol(false);}}} style={{width:"100%",padding:"7px 10px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:12,outline:"none",marginBottom:10,boxSizing:"border-box"}} />
                  <div style={{display:"grid",gridTemplateColumns:"repeat(10,1fr)",gap:4,marginBottom:10}}>
                    {CONFIG_COLORS.map(c=><div key={c} onClick={()=>setNewCol(p=>({...p,color:c}))} style={{width:"100%",aspectRatio:"1",borderRadius:4,background:c,cursor:"pointer",outline:newCol.color===c?"2.5px solid #1c1917":"2.5px solid transparent",outlineOffset:1}} />)}
                  </div>
                  <div style={{display:"flex",gap:6,marginBottom:newCol.type==="assigned"?10:0}}>
                    {["generic","assigned"].map(t=><button key={t} onClick={()=>setNewCol(p=>({...p,type:t}))} style={{flex:1,padding:"6px",border:"1px solid",borderColor:newCol.type===t?"#6366f1":"#e2e8f0",background:newCol.type===t?"#6366f1":"#fff",color:newCol.type===t?"#fff":"#64748b",borderRadius:6,fontSize:11,cursor:"pointer",fontWeight:600}}>{t==="generic"?"Genérica":"Asignada"}</button>)}
                  </div>
                  {newCol.type==="assigned" && <select value={newCol.assignedTo||""} onChange={e=>setNewCol(p=>({...p,assignedTo:Number(e.target.value)}))} style={{width:"100%",padding:"7px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:12,outline:"none",marginBottom:10}}><option value="">Seleccionar agente</option>{agentesOf(cfgActiveAgency).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}</select>}
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={async ()=>{
                      if(!newCol.title.trim()) return;
                      try {
                        const agencia = cfgColumns.find(c=>c.agency===cfgActiveAgency);
                        const nueva = await columnas.crear({ titulo: newCol.title, color: newCol.color, agencia_id: agencia?.agencia_id || 1, tipo: newCol.type, agente_id: newCol.assignedTo || null });
                        setCfgColumns(prev=>[...prev,{id:nueva.id,title:newCol.title,color:newCol.color,agency:cfgActiveAgency,order:agencyCols.length,type:newCol.type,assignedTo:newCol.type==="assigned"?newCol.assignedTo:null,visibleToAll:false}]);
                        setNewCol({title:"",color:"#6366f1",type:"generic",assignedTo:null});
                        setShowNewCol(false);
                      } catch(err){ alert("Error: "+err.message); }
                    }} style={{flex:1,padding:"7px",background:"#6366f1",color:"#fff",border:"none",borderRadius:6,fontSize:12,cursor:"pointer",fontWeight:700}}>Crear</button>
                    <button onClick={()=>setShowNewCol(false)} style={{flex:1,padding:"7px",background:"#f1f5f9",color:"#64748b",border:"none",borderRadius:6,fontSize:12,cursor:"pointer"}}>Cancelar</button>
                  </div>
                </div>
              </div>
            )}
          </div>
          {editCol && <CfgModal onClose={()=>setEditCol(null)}>
            <h3 style={{margin:"0 0 20px",fontSize:16,fontWeight:800,color:"#1e293b"}}>Editar columna</h3>
            <div style={{marginBottom:14}}><label style={{fontSize:11,fontWeight:700,color:"#64748b",display:"block",marginBottom:6}}>NOMBRE</label><input value={editCol.title} onChange={e=>setEditCol(p=>({...p,title:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}} /></div>
            <div style={{marginBottom:14}}><label style={{fontSize:11,fontWeight:700,color:"#64748b",display:"block",marginBottom:8}}>COLOR</label><div style={{display:"grid",gridTemplateColumns:"repeat(10,1fr)",gap:5}}>{CONFIG_COLORS.map(c=><div key={c} onClick={()=>setEditCol(p=>({...p,color:c}))} style={{width:"100%",aspectRatio:"1",borderRadius:4,background:c,cursor:"pointer",outline:editCol.color===c?"2.5px solid #1c1917":"2.5px solid transparent",outlineOffset:1}} />)}</div></div>
            <div style={{marginBottom:14}}><label style={{fontSize:11,fontWeight:700,color:"#64748b",display:"block",marginBottom:6}}>TIPO</label><div style={{display:"flex",gap:8}}>{["generic","assigned"].map(t=><button key={t} onClick={()=>setEditCol(p=>({...p,type:t}))} style={{flex:1,padding:"8px",border:"1px solid",borderColor:editCol.type===t?"#6366f1":"#e2e8f0",background:editCol.type===t?"#6366f1":"#fff",color:editCol.type===t?"#fff":"#64748b",borderRadius:8,fontSize:12,cursor:"pointer",fontWeight:600}}>{t==="generic"?"Genérica":"Asignada"}</button>)}</div></div>
            {editCol.type==="assigned" && <><div style={{marginBottom:14}}><label style={{fontSize:11,fontWeight:700,color:"#64748b",display:"block",marginBottom:6}}>AGENTE</label><select value={editCol.assignedTo||""} onChange={e=>setEditCol(p=>({...p,assignedTo:Number(e.target.value)}))} style={{width:"100%",padding:"10px 12px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:13,outline:"none"}}><option value="">Seleccionar</option>{agentesOf(editCol.agency).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}</select></div><div style={{marginBottom:14,padding:12,background:"#f8fafc",borderRadius:8}}><CfgToggle value={editCol.visibleToAll} onChange={v=>setEditCol(p=>({...p,visibleToAll:v}))} label="Visible para el equipo de esta agencia" /></div></>}
            <div style={{marginBottom:20,padding:12,background:"#fff7ed",borderRadius:8,border:"1px solid #fed7aa"}}>
              <CfgToggle value={editCol.stopAI||false} onChange={v=>setEditCol(p=>({...p,stopAI:v}))} label="🤖 Stop AI — no reenviar mensajes a n8n cuando el cliente esté en esta columna" />
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={async ()=>{
                try {
                  await columnas.actualizar(editCol.id, { titulo: editCol.title, color: editCol.color, tipo: editCol.type, agente_id: editCol.type==="assigned"?editCol.assignedTo:null, visible_todos: editCol.visibleToAll });
                  setCfgColumns(prev=>prev.map(c=>c.id===editCol.id?{...editCol,assignedTo:editCol.type==="assigned"?editCol.assignedTo:null}:c));
                  setEditCol(null);
                } catch(err){ alert("Error: "+err.message); }
              }} style={{flex:1,padding:"10px",background:"#6366f1",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer"}}>Guardar</button>
              <button onClick={()=>setEditCol(null)} style={{flex:1,padding:"10px",background:"#f1f5f9",color:"#475569",border:"none",borderRadius:8,fontSize:13,cursor:"pointer"}}>Cancelar</button>
            </div>
          </CfgModal>}
        </div>
      );

      // ── FUENTES ──
      case "fuentes": return (
        <div>
          <div style={{display:"flex",marginBottom:24,borderBottom:"1px solid #e2e8f0"}}>
            {AGENCIES_LIST.map(a=><button key={a} onClick={()=>setCfgActiveAgency(a)} style={{padding:"8px 18px",border:"none",background:"transparent",fontSize:13,fontWeight:cfgActiveAgency===a?700:500,color:cfgActiveAgency===a?"#6366f1":"#64748b",borderBottom:cfgActiveAgency===a?"2px solid #6366f1":"2px solid transparent",cursor:"pointer"}}>{a.replace("Rs Viajes ","")}</button>)}
          </div>

          {/* Webhook n8n por agencia */}
          <div style={{background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:12,padding:"16px 20px",marginBottom:20}}>
            <h4 style={{margin:"0 0 8px",fontSize:13,fontWeight:800,color:"#059669"}}>🔗 Webhook n8n — {cfgActiveAgency.replace("Rs Viajes ","")}</h4>
            <p style={{fontSize:11,color:"#64748b",margin:"0 0 10px"}}>Todos los mensajes entrantes de esta agencia se enviarán a esta URL (si la columna no tiene Stop AI activado)</p>
            <WebhookN8nInput agencia={cfgActiveAgency} agenciaId={AGENCY_IDS[cfgActiveAgency]} />
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:24}}>
            {cfgSources.filter(s=>s.agency===cfgActiveAgency).map(s=>(
              <div key={s.id} style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,padding:"14px 16px",display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:42,height:42,borderRadius:10,background:PLATFORM_COLORS[s.platform]+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>{PLATFORM_ICONS[s.platform]}</div>
                <div style={{flex:1}}><p style={{margin:0,fontWeight:700,fontSize:13,color:"#1e293b"}}>{s.name}</p><p style={{margin:0,fontSize:11,color:"#94a3b8",textTransform:"capitalize"}}>{s.platform}</p></div>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:11,fontWeight:700,color:s.connected?"#059669":"#94a3b8"}}>{s.connected?"● Conectado":"○ Desconectado"}</span>
                  <button onClick={async ()=>{ try { await fuentes.actualizar(s.id, { conectado: !s.connected }); setCfgSources(prev=>prev.map(src=>src.id===s.id?{...src,connected:!src.connected}:src)); } catch(err){ alert("Error: "+err.message); }}} style={{padding:"6px 14px",background:s.connected?"#fef2f2":"#f0fdf4",border:"none",borderRadius:8,fontSize:12,color:s.connected?"#ef4444":"#059669",cursor:"pointer",fontWeight:600}}>{s.connected?"Desconectar":"Conectar"}</button>
                </div>
              </div>
            ))}
          </div>
          <h4 style={{fontSize:12,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:0.5,margin:"0 0 12px"}}>Añadir fuente</h4>
          <div style={{display:"flex",gap:10}}>
            {["whatsapp","messenger","instagram","telegram"].map(p=>(
              <button key={p} onClick={async ()=>{const name=prompt(`Cuenta de ${p}:`);if(!name)return; try { const agenciaId = AGENCY_IDS[cfgActiveAgency]||1; const nueva = await fuentes.crear({ agencia_id: agenciaId, plataforma:p, nombre:name }); setCfgSources(prev=>[...prev,{id:nueva.id,agency:cfgActiveAgency,platform:p,name,connected:false}]); } catch(err){ alert("Error: "+err.message); }}} style={{flex:1,padding:"14px",background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:6,transition:"all 0.15s"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=PLATFORM_COLORS[p]||"#94a3b8";e.currentTarget.style.background=(PLATFORM_COLORS[p]||"#94a3b8")+"08";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor="#e2e8f0";e.currentTarget.style.background="#fff";}}
              ><span style={{fontSize:24}}>{PLATFORM_ICONS[p]||"📱"}</span><span style={{fontSize:11,fontWeight:600,color:"#475569",textTransform:"capitalize"}}>{p}</span></button>
            ))}
          </div>
        </div>
      );

      // ── IDENTIFICADORES ──
      case "identificadores": return (
        <IdentificadoresSection
          cfgFields={cfgFields}
          setCfgFields={setCfgFields}
          cfgColumns={cfgColumns}
        />
      );

      // ── PERMISOS ESTADÍSTICAS ──
      case "permisos": return (
        <div>
          <p style={{fontSize:13,color:"#64748b",margin:"0 0 20px",lineHeight:1.6}}>Decide qué estadísticas ve cada rol.</p>
          <div style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,overflow:"hidden"}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr repeat(4,90px)",padding:"10px 16px",background:"#f8fafc",borderBottom:"1px solid #e2e8f0",gap:8}}>
              <span style={{fontSize:11,fontWeight:700,color:"#64748b"}}>ESTADÍSTICA</span>
              {["Admin","Manager","Supervisor","Agente"].map(r=><span key={r} style={{fontSize:11,fontWeight:700,color:"#64748b",textAlign:"center"}}>{r.toUpperCase()}</span>)}
            </div>
            {statPerms.map((p,i)=>(
              <div key={p.id} style={{display:"grid",gridTemplateColumns:"1fr repeat(4,90px)",padding:"12px 16px",borderBottom:i<statPerms.length-1?"1px solid #f1f5f9":"none",alignItems:"center",gap:8}}>
                <span style={{fontSize:13,color:"#1e293b"}}>{p.label}</span>
                {["admin","manager","supervisor","agente"].map(role=>(
                  <div key={role} style={{display:"flex",justifyContent:"center"}}>
                    {role==="admin" ? <span style={{fontSize:15,color:"#059669"}}>✓</span> : <CfgToggle value={p[role]} onChange={v=>setStatPerms(prev=>prev.map(sp=>sp.id===p.id?{...sp,[role]:v}:sp))} />}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      );

      // ── GENERAL ──
      case "disparadores": return <DisparadoresPanel cfgColumns={cfgColumns} />;

      case "general": return (
        <div>
          <div style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,padding:"20px 24px",marginBottom:16}}>
            <h4 style={{margin:"0 0 16px",fontSize:13,fontWeight:800,color:"#1e293b"}}>Información del sistema</h4>
            {[{label:"Versión CRM",value:"1.0.0"},{label:"Base de datos",value:"PostgreSQL 16"},{label:"Servidor",value:"72.60.66.72:8080"},{label:"Backups",value:"Diarios a las 3:00 AM"}].map(item=>(
              <div key={item.label} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #f1f5f9"}}>
                <span style={{fontSize:13,color:"#64748b"}}>{item.label}</span>
                <span style={{fontSize:13,fontWeight:600,color:"#1e293b"}}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      );

      // ── TARJETAS ──
      case "tarjetas": return (
        <TarjetasSection cfgFields={cfgFields} />
      );

      default: return null;
    }
  };

  return (
    <div style={{flex:1,display:"flex",overflow:"hidden",background:T.bg}}>
      {/* Sidebar config */}
      <div style={{width:210,flexShrink:0,background:"#fff",borderRight:`1px solid ${T.border}`,padding:"20px 12px",display:"flex",flexDirection:"column",gap:4}}>
        <p style={{fontSize:10,fontWeight:800,color:T.textMuted,textTransform:"uppercase",letterSpacing:0.8,margin:"0 0 10px 8px"}}>Configuración</p>
        {allowedSections.map(s=>(
          <button key={s.id} onClick={()=>setActiveSection(s.id)}
            style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",border:"none",borderRadius:12,background:activeId===s.id?T.primaryLt:"transparent",color:activeId===s.id?T.primary:T.textSub,fontSize:13,fontWeight:activeId===s.id?700:500,cursor:"pointer",textAlign:"left",transition:"all 0.15s"}}
            onMouseEnter={e=>{if(activeId!==s.id)e.currentTarget.style.background=T.panel;}}
            onMouseLeave={e=>{if(activeId!==s.id)e.currentTarget.style.background="transparent";}}
          ><span style={{fontSize:16}}>{s.icon}</span>{s.label}</button>
        ))}
      </div>
      {/* Content */}
      <div style={{flex:1,overflowY:"auto",padding:28}}>
        <div style={{maxWidth:900}}>
          <h2 style={{fontSize:20,fontWeight:800,color:T.textPrimary,margin:"0 0 6px",display:"flex",alignItems:"center",gap:10}}><span>{section?.icon}</span>{section?.label}</h2>
          <div style={{height:1,background:T.border,margin:"16px 0 24px"}} />
          {renderSection()}
        </div>
      </div>
    </div>
  );
}

// ─── ESTADOS DE CARGA ─────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", flexDirection: "column", gap: 12 }}>
      <div style={{ width: 36, height: 36, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.primary}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <p style={{ fontSize: 13, color: T.textMuted, margin: 0 }}>Cargando...</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ErrorMsg({ mensaje, onRetry }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", flexDirection: "column", gap: 12 }}>
      <span style={{ fontSize: 32 }}>⚠️</span>
      <p style={{ fontSize: 14, color: "#dc2626", fontWeight: 700, margin: 0 }}>{mensaje}</p>
      {onRetry && <button onClick={onRetry} style={{ padding: "8px 20px", background: T.primary, color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>Reintentar</button>}
    </div>
  );
}

// ─── HOOK: datos con carga automática ─────────────────────────────────────────

function useFetch(fn, deps = []) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fn();
      setData(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, deps);

  useEffect(() => { cargar(); }, [cargar]);

  return { data, loading, error, recargar: cargar };
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────

function Login({ onLogin }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd]   = useState(false);
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [focused, setFocused]   = useState(null);

  const handleLogin = async () => {
    if (!email || !password) { setError("Completa todos los campos"); return; }
    setLoading(true); setError("");
    try {
      const user = await auth.login(email, password);
      onLogin(user);
    } catch (err) {
      setError(err.message || "Email o contraseña incorrectos");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", fontFamily: "'DM Sans', sans-serif", position: "relative", overflow: "hidden" }}>
      {/* decorative blobs */}
      <div style={{ position:"absolute", top:-180, left:-120, width:500, height:500, borderRadius:"50%", background:`radial-gradient(circle, ${T.primary}18 0%, transparent 65%)`, pointerEvents:"none" }} />
      <div style={{ position:"absolute", bottom:-100, right:-80, width:360, height:360, borderRadius:"50%", background:`radial-gradient(circle, ${T.accent}12 0%, transparent 65%)`, pointerEvents:"none" }} />

      {/* Left branding */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: "60px 80px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 48 }}>
          <div style={{ width: 44, height: 44, borderRadius: 14, background: `linear-gradient(135deg, ${T.primary}, ${T.sidebarBot})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, boxShadow:`0 4px 16px ${T.primary}44` }}>✈</div>
          <span style={{ color: T.textPrimary, fontWeight: 800, fontSize: 20, letterSpacing:-0.5 }}>Zenda CRM</span>
        </div>
        <h1 style={{ color: T.textPrimary, fontSize: 42, fontWeight: 900, lineHeight: 1.1, margin: "0 0 18px", letterSpacing: -1.5 }}>
          Tu equipo,<br /><span style={{ color: T.primary }}>en un solo lugar.</span>
        </h1>
        <p style={{ color: T.textSub, fontSize: 15, lineHeight: 1.7, maxWidth: 380, margin: "0 0 40px" }}>
          Gestiona tus tres agencias, sigue cada lead y automatiza tus seguimientos.
        </p>
        {["Rs Viajes Periodismo", "Rs Viajes Independencia", "Rs Viajes Centro"].map((a, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", borderRadius: 14, background: "#fff", border: `1px solid ${T.border}`, marginBottom: 8, boxShadow:"0 1px 4px rgba(0,0,0,0.05)" }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: [T.primary,"#10b981","#f59e0b"][i], flexShrink: 0 }} />
            <span style={{ color: T.textPrimary, fontSize: 13, fontWeight: 600 }}>{a}</span>
          </div>
        ))}
      </div>

      {/* Right form */}
      <div style={{ width: 440, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 40, borderLeft: `1px solid ${T.border}`, background:"#fff" }}>
        <div style={{ width: "100%" }}>
          <h2 style={{ color: T.textPrimary, fontWeight: 900, fontSize: 28, margin: "0 0 6px", letterSpacing:-0.5 }}>Iniciar sesión</h2>
          <p style={{ color: T.textMuted, fontSize: 14, margin: "0 0 32px" }}>Entra con tu cuenta</p>

          {[{field:"email",label:"EMAIL",ph:"tu@rsviajes.com"},{field:"password",label:"CONTRASEÑA",ph:"••••••••"}].map(f => (
            <div key={f.field} style={{ marginBottom: 16 }}>
              <label style={{ display: "block", color: T.textSub, fontSize: 11, fontWeight: 700, marginBottom: 6, letterSpacing:0.5 }}>{f.label}</label>
              <div style={{ position: "relative" }}>
                <input
                  type={f.field === "password" ? (showPwd ? "text" : "password") : "email"}
                  value={f.field === "email" ? email : password}
                  onChange={e => { f.field === "email" ? setEmail(e.target.value) : setPassword(e.target.value); setError(""); }}
                  onFocus={() => setFocused(f.field)}
                  onBlur={() => setFocused(null)}
                  onKeyDown={e => e.key === "Enter" && handleLogin()}
                  placeholder={f.ph}
                  style={{ width: "100%", padding: f.field === "password" ? "14px 48px 14px 16px" : "14px 16px", background: focused===f.field?"#fff":T.panel, border: `1.5px solid ${focused === f.field ? T.primary : T.border}`, borderRadius: 12, color: T.textPrimary, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", transition:"all 0.15s" }}
                />
                {f.field === "password" && (
                  <button onClick={() => setShowPwd(!showPwd)} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: T.textMuted, fontSize: 16 }}>
                    {showPwd ? "🙈" : "👁"}
                  </button>
                )}
              </div>
            </div>
          ))}

          {error && (
            <div style={{ marginBottom: 16, padding: "10px 14px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, color: "#dc2626", fontSize: 13, fontWeight:600 }}>
              ⚠ {error}
            </div>
          )}

          <button onClick={handleLogin} disabled={loading} style={{ width: "100%", padding: "15px", background: loading ? T.border : `linear-gradient(135deg, ${T.primary}, ${T.sidebarBot})`, border: "none", borderRadius: 14, color: loading ? T.textMuted : "#fff", fontSize: 15, fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", boxShadow: loading?"none":`0 4px 16px ${T.primary}44`, transition:"all 0.2s" }}>
            {loading ? "Verificando..." : "Entrar →"}
          </button>
        </div>
      </div>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;0,9..40,900&display=swap'); * { box-sizing: border-box; } input::placeholder { color: #a8a29e; }`}</style>
    </div>
  );
}

// ─── SIDEBAR ──────────────────────────────────────────────────────────────────

const NAV = [
  { id: "kanban", icon: "⊞", label: "Tablero",       roles: ["admin","manager","supervisor","agente"] },
  { id: "chats",  icon: "💬", label: "Chats",         roles: ["admin","manager","supervisor","agente"] },
  { id: "stats",  icon: "📈", label: "Estadísticas",  roles: ["admin","manager","supervisor","agente"] },
  { id: "config", icon: "⚙", label: "Configuración", roles: ["admin","manager"] },
];

const ROLE_COLORS = { admin: "#f97316", manager: "#ef4444", supervisor: "#8b5cf6", agente: "#3b82f6" };
const ROLE_ICONS  = { admin: "⚙", manager: "🎯", supervisor: "👁", agente: "💼" };

function Avatar({ name, size = 28 }) {
  const color = ROLE_COLORS[name] || "#78716c";
  return <div style={{ width: size, height: size, borderRadius: "50%", background: color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.36, fontWeight: 700, color: "#fff", flexShrink: 0 }}>{name?.split(" ").map(n => n[0]).join("").slice(0,2)}</div>;
}

// ─── NAV ICONS (SVG inline para estilo limpio) ────────────────────────────────
const NAV_ICONS = {
  kanban: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  chats: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  stats: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>
    </svg>
  ),
  config: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
};

const NAV_LABELS = { kanban:"Tablero", chats:"Chats", stats:"Reportes", config:"Ajustes" };

function Sidebar({ active, setActive, user, unreadCount }) {
  const allowed = NAV.filter(n => n.roles.includes(user.rol));
  const initials = user.nombre?.split(" ").map(n=>n[0]).join("").slice(0,2) || "U";
  return (
    <div style={{
      width: 180,
      background: `linear-gradient(160deg, #f97316 0%, #e8420a 45%, #9b1fa8 100%)`,
      display: "flex", flexDirection: "column",
      padding: "20px 12px 16px", gap: 4, flexShrink: 0,
    }}>
      {/* Logo + nombre app */}
      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"4px 8px", marginBottom:24 }}>
        <div style={{ width:38, height:38, borderRadius:12, background:"rgba(255,255,255,0.25)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>✈</div>
        <span style={{ color:"#fff", fontWeight:800, fontSize:15, letterSpacing:-0.3 }}>Zenda CRM</span>
      </div>

      {/* Nav items */}
      {allowed.map(item => (
        <button key={item.id} onClick={() => setActive(item.id)}
          style={{
            width:"100%", padding:"10px 12px", borderRadius:12, border:"none",
            background: active===item.id ? "rgba(255,255,255,0.22)" : "transparent",
            color: active===item.id ? "#fff" : "rgba(255,255,255,0.7)",
            cursor:"pointer", display:"flex", alignItems:"center", gap:12,
            position:"relative", transition:"all 0.15s", textAlign:"left",
          }}
          onMouseEnter={e => { if(active!==item.id) e.currentTarget.style.background="rgba(255,255,255,0.1)"; }}
          onMouseLeave={e => { if(active!==item.id) e.currentTarget.style.background="transparent"; }}
        >
          <span style={{ display:"flex", alignItems:"center", flexShrink:0 }}>{NAV_ICONS[item.id]}</span>
          <span style={{ fontSize:13, fontWeight: active===item.id?700:500 }}>{NAV_LABELS[item.id]}</span>
          {item.id === "chats" && unreadCount > 0 && (
            <span style={{ marginLeft:"auto", background:"#fff", color:T.primary, fontSize:9, fontWeight:800, borderRadius:10, padding:"1px 6px", minWidth:16, textAlign:"center" }}>{unreadCount}</span>
          )}
        </button>
      ))}

      {/* Avatar bottom */}
      <div style={{ marginTop:"auto", display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:12, background:"rgba(255,255,255,0.15)" }}>
        <div style={{ width:32, height:32, borderRadius:"50%", background:"rgba(255,255,255,0.3)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:800, color:"#fff", flexShrink:0 }}>
          {initials}
        </div>
        <div style={{ minWidth:0 }}>
          <p style={{ margin:0, fontSize:12, fontWeight:700, color:"#fff", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{user.nombre?.split(" ")[0]}</p>
          <p style={{ margin:0, fontSize:10, color:"rgba(255,255,255,0.65)", textTransform:"capitalize" }}>{user.rol}</p>
        </div>
      </div>
    </div>
  );
}

// ─── KANBAN ───────────────────────────────────────────────────────────────────

function KanbanView({ user }) {
  const [activeAgency, setActiveAgency] = useState(user.agencia || "Rs Viajes Independencia");
  const [dragging, setDragging]         = useState(null);
  const [openCard, setOpenCard]         = useState(null);
  const [selected, setSelected]         = useState(new Set());
  const [showFilters, setShowFilters]   = useState(false);
  const [deleting, setDeleting]         = useState(false);
  const [cardFieldIds]                  = useState(loadCardFields); // campos configurados para tarjeta

  // Filtros
  const [search, setSearch]       = useState("");
  const [filtroCanal, setFiltroCanal]   = useState("");
  const [filtroDesde, setFiltroDesde]   = useState("");
  const [filtroHasta, setFiltroHasta]   = useState("");

  const [addingToCol, setAddingToCol] = useState(null);
  const [newClientName, setNewClientName] = useState("");
  const canDelete = user.rol === "admin" || user.rol === "manager";

  const agencias = user.rol === "agente"
    ? [user.agencia]
    : ["Rs Viajes Periodismo", "Rs Viajes Independencia", "Rs Viajes Centro"];

  const { data: cols,  loading: loadingCols  } = useFetch(() => columnas.listar());
  const { data: cards, loading: loadingCards, recargar } = useFetch(
    () => clientes.listar({ agencia_id: activeAgency }),
    [activeAgency]
  );

  const agencyCols = (cols || [])
    .filter(c => c.agencia === activeAgency || c.agencia_id === agencias.indexOf(activeAgency) + 1)
    .sort((a, b) => a.orden - b.orden);

  // Aplicar filtros a las tarjetas
  const filtrarCard = (card) => {
    const q = search.toLowerCase();
    if (q && !card.nombre?.toLowerCase().includes(q) &&
             !card.telefono?.includes(q) &&
             !String(card.id).includes(q) &&
             !card.destino?.toLowerCase().includes(q)) return false;
    if (filtroCanal && card.canal !== filtroCanal) return false;
    if (filtroDesde) {
      const ultimo = card.ultimo_mensaje ? new Date(card.ultimo_mensaje) : null;
      if (!ultimo || ultimo < new Date(filtroDesde)) return false;
    }
    if (filtroHasta) {
      const ultimo = card.ultimo_mensaje ? new Date(card.ultimo_mensaje) : null;
      if (!ultimo || ultimo > new Date(filtroHasta + "T23:59:59")) return false;
    }
    return true;
  };

  const allFiltered = (cards || []).filter(filtrarCard);
  const hasFilters  = search || filtroCanal || filtroDesde || filtroHasta;
  const hasSelected = selected.size > 0;

  const clearFilters = () => {
    setSearch(""); setFiltroCanal(""); setFiltroDesde(""); setFiltroHasta("");
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const selectAll = () => setSelected(new Set(allFiltered.map(c => c.id)));
  const clearSelected = () => setSelected(new Set());

  const handleDrop = async (columna_id) => {
    if (!dragging) return;
    try {
      await clientes.moverColumna(dragging, columna_id);
      recargar();
    } catch (err) { alert("Error moviendo cliente: " + err.message); }
    setDragging(null);
  };

  const handleDeleteSelected = async () => {
    if (!canDelete) return;
    const count = selected.size;
    if (!window.confirm(`¿Eliminar ${count} cliente${count>1?"s":""} permanentemente? Esta acción no se puede deshacer.`)) return;
    setDeleting(true);
    try {
      await Promise.all([...selected].map(id => clientes.eliminar(id)));
      clearSelected();
      recargar();
    } catch (err) { alert("Error eliminando: " + err.message); }
    setDeleting(false);
  };

  if (loadingCols || loadingCards) return <Spinner />;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: T.bg }}>
      {/* Agency tabs + barra de búsqueda */}
      <div style={{ background: "#fff", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        {/* Tabs */}
        <div style={{ padding: "0 24px", display: "flex", alignItems: "center", borderBottom: `1px solid ${T.panel}` }}>
          {agencias.map(a => (
            <button key={a} onClick={() => { setActiveAgency(a); clearSelected(); }} style={{ padding: "12px 20px", border: "none", background: "transparent", fontSize: 13, fontWeight: activeAgency===a?800:500, color: activeAgency===a?T.primary:T.textSub, borderBottom: activeAgency===a?`2.5px solid ${T.primary}`:"2.5px solid transparent", cursor: "pointer", transition:"all 0.15s" }}>
              {a.replace("Rs Viajes ","")}
            </button>
          ))}
          <button onClick={recargar} style={{ marginLeft: "auto", background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 14, padding: "0 16px" }}>↻</button>
        </div>

        {/* Barra de búsqueda y filtros */}
        <div style={{ padding: "10px 24px", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: T.textMuted }}>🔍</span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre, teléfono, #ID o destino..." style={{ width: "100%", padding: "9px 12px 9px 34px", border: `1px solid ${T.border}`, borderRadius: 12, fontSize: 13, outline: "none", boxSizing: "border-box", background: T.panel, color: T.textPrimary }} />
          </div>

          <button onClick={() => setShowFilters(!showFilters)} style={{ padding: "9px 14px", border: "1px solid", borderColor: showFilters?T.primary:T.border, background: showFilters?T.primaryLt:"#fff", color: showFilters?T.primary:T.textSub, borderRadius: 12, fontSize: 12, cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
            ⚙ Filtros {hasFilters && <span style={{ background: "#ef4444", color: "#fff", borderRadius: "50%", width: 16, height: 16, fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>!</span>}
          </button>

          {hasFilters && <button onClick={clearFilters} style={{ padding: "9px 12px", border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", borderRadius: 12, fontSize: 12, cursor: "pointer", fontWeight: 600 }}>✕ Limpiar</button>}

          {hasFilters && (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: T.textSub }}>{allFiltered.length} resultado{allFiltered.length!==1?"s":""}</span>
              <button onClick={selectAll} style={{ padding: "6px 12px", border: `1px solid ${T.border}`, background: "#fff", color: T.textSub, borderRadius: 8, fontSize: 11, cursor: "pointer", fontWeight: 600 }}>Seleccionar todos ({allFiltered.length})</button>
            </div>
          )}

          {hasSelected && (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.primary }}>{selected.size} seleccionado{selected.size!==1?"s":""}</span>
              <button onClick={clearSelected} style={{ padding: "6px 10px", border: `1px solid ${T.border}`, background: "#fff", color: T.textSub, borderRadius: 8, fontSize: 11, cursor: "pointer" }}>Deseleccionar</button>
              {canDelete && (
                <button onClick={handleDeleteSelected} disabled={deleting} style={{ padding: "6px 12px", border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", borderRadius: 8, fontSize: 11, cursor: "pointer", fontWeight: 700 }}>
                  {deleting ? "Eliminando..." : `🗑 Eliminar (${selected.size})`}
                </button>
              )}
            </div>
          )}
        </div>

        {showFilters && (
          <div style={{ padding: "0 24px 12px", display: "flex", gap: 12, flexWrap: "wrap", borderTop: `1px solid ${T.panel}` }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: T.textSub, display: "block", marginBottom: 4, letterSpacing:0.4 }}>CANAL</label>
              <select value={filtroCanal} onChange={e => setFiltroCanal(e.target.value)} style={{ padding: "7px 10px", border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 12, outline: "none", background:"#fff" }}>
                <option value="">Todos</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="instagram">Instagram</option>
                <option value="messenger">Messenger</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: T.textSub, display: "block", marginBottom: 4, letterSpacing:0.4 }}>ÚLTIMO MENSAJE DESDE</label>
              <input type="date" value={filtroDesde} onChange={e => setFiltroDesde(e.target.value)} style={{ padding: "7px 10px", border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 12, outline: "none" }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: T.textSub, display: "block", marginBottom: 4, letterSpacing:0.4 }}>HASTA</label>
              <input type="date" value={filtroHasta} onChange={e => setFiltroHasta(e.target.value)} style={{ padding: "7px 10px", border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 12, outline: "none" }} />
            </div>
          </div>
        )}
      </div>

      {/* Board */}
      <div style={{ flex: 1, overflowX: "auto", padding: "20px 24px", display: "flex", gap: 14, alignItems: "flex-start" }}>
        {agencyCols.map(col => {
          const colCards = (cards || []).filter(c => c.columna_id === col.id).filter(filtrarCard);
          return (
            <div key={col.id} onDrop={() => handleDrop(col.id)} onDragOver={e => e.preventDefault()}
              style={{ width: 248, flexShrink: 0, display: "flex", flexDirection: "column" }}>
              {/* Column header */}
              <div style={{ padding: "10px 14px", borderRadius: "14px 14px 0 0", background: "#fff", borderTop: `3px solid ${col.color}`, border: `1px solid ${T.border}`, borderTopColor: col.color }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input type="checkbox"
                      checked={colCards.length > 0 && colCards.every(c => selected.has(c.id))}
                      onChange={() => {
                        const allSelected = colCards.every(c => selected.has(c.id));
                        setSelected(prev => {
                          const n = new Set(prev);
                          colCards.forEach(c => allSelected ? n.delete(c.id) : n.add(c.id));
                          return n;
                        });
                      }}
                      title="Seleccionar toda la columna"
                      style={{ accentColor: col.color, width: 13, height: 13, cursor: "pointer" }}
                    />
                    <h3 style={{ fontSize: 11, fontWeight: 800, color: T.textPrimary, margin: 0, textTransform: "uppercase", letterSpacing: 0.4 }}>{col.titulo}</h3>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 800, background: col.color+"18", color: col.color, padding: "2px 9px", borderRadius: 20 }}>{colCards.length}</span>
                </div>
              </div>
              {/* Column body */}
              <div style={{ flex: 1, overflowY: "auto", padding: 10, background: T.panel, border: `1px solid ${T.border}`, borderTop: "none", borderRadius: "0 0 14px 14px", minHeight: 80 }}>
                {colCards.map(card => (
                  <div key={card.id} draggable onDragStart={() => setDragging(card.id)}
                    style={{ background: "#fff", borderRadius: 12, padding: "12px 14px", marginBottom: 8, boxShadow: selected.has(card.id)?`0 0 0 2px ${T.primary}`:"0 1px 4px rgba(0,0,0,0.07)", cursor: "grab", border: selected.has(card.id)?`1.5px solid ${T.primary}`:`1px solid ${T.border}`, transition: "box-shadow 0.15s", position: "relative" }}
                    onMouseEnter={e => { if(!selected.has(card.id)) e.currentTarget.style.boxShadow="0 4px 14px rgba(0,0,0,0.11)"; }}
                    onMouseLeave={e => { if(!selected.has(card.id)) e.currentTarget.style.boxShadow="0 1px 4px rgba(0,0,0,0.07)"; }}
                  >
                    {(hasFilters || hasSelected) && (
                      <input type="checkbox" checked={selected.has(card.id)} onChange={() => toggleSelect(card.id)}
                        onClick={e => e.stopPropagation()}
                        style={{ position: "absolute", top: 10, right: 10, accentColor: T.primary, width: 14, height: 14 }} />
                    )}
                    <div onClick={() => setOpenCard(card)}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 9, color: T.textMuted, fontWeight: 600 }}>#{card.id}</span>
                        {card.ultimo_mensaje && <span style={{ fontSize: 9, color: T.textMuted }}>{new Date(card.ultimo_mensaje).toLocaleTimeString("es-MX",{hour:"2-digit",minute:"2-digit"})}</span>}
                      </div>
                      <p style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary, margin: "0 0 4px" }}>{card.nombre || card.telefono || "Sin nombre"}</p>
                      {card.telefono && <p style={{ fontSize: 11, color: T.textSub, margin: "0 0 5px" }}>{card.telefono}</p>}
                      {/* Campos configurados para tarjeta */}
                      {cardFieldIds.length > 0 && (card.campos_preview || []).filter(f=>cardFieldIds.includes(f.campo_id)&&f.valor).map(f=>(
                        <div key={f.campo_id} style={{display:"flex",gap:4,alignItems:"baseline",marginBottom:2}}>
                          <span style={{fontSize:9,color:T.textMuted,fontWeight:600,textTransform:"uppercase",letterSpacing:0.3,flexShrink:0}}>{f.label}:</span>
                          <span style={{fontSize:11,color:T.textSub,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.valor}</span>
                        </div>
                      ))}
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop:4 }}>
                        {card.no_leidos > 0 && <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 10, background: "#fef2f2", color: "#dc2626", fontWeight: 800 }}>{card.no_leidos} sin leer</span>}
                        {card.canal && <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 10, background: T.panel, color: T.textSub }}>{card.canal}</span>}
                      </div>
                    </div>
                  </div>
                ))}
                <button onClick={() => { setAddingToCol(col); setNewClientName(""); }}
                  style={{ width: "100%", padding: "9px", background: "transparent", border: `1.5px dashed ${T.borderDk}`, borderRadius: 10, color: T.primary, fontSize: 12, cursor: "pointer", fontWeight: 600, transition:"all 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background=T.primaryLt}
                  onMouseLeave={e => e.currentTarget.style.background="transparent"}
                >
                  + Añadir cliente
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {openCard && <ClienteModal card={openCard} user={user} onClose={() => { setOpenCard(null); recargar(); }} />}

      {/* Modal añadir cliente */}
      {addingToCol && (
        <div onClick={()=>setAddingToCol(null)} style={{position:"fixed",inset:0,background:"rgba(28,25,23,0.4)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,backdropFilter:"blur(4px)"}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:20,padding:28,width:400,boxShadow:"0 24px 60px rgba(0,0,0,0.18)",border:`1px solid ${T.border}`}}>
            <h3 style={{margin:"0 0 6px",fontSize:16,fontWeight:800,color:T.textPrimary}}>Añadir cliente</h3>
            <p style={{margin:"0 0 20px",fontSize:12,color:T.textSub}}>Columna: <strong>{addingToCol.titulo}</strong></p>
            <label style={{fontSize:11,fontWeight:700,color:T.textSub,display:"block",marginBottom:6,letterSpacing:0.4}}>NOMBRE DEL CLIENTE</label>
            <input
              autoFocus
              value={newClientName}
              onChange={e=>setNewClientName(e.target.value)}
              onKeyDown={async e=>{
                if(e.key==="Enter" && newClientName.trim()) {
                  try { await clientes.crear({ agencia_id: agencias.indexOf(activeAgency)+1, columna_id: addingToCol.id, nombre: newClientName.trim() }); recargar(); setAddingToCol(null); } catch(err){ alert(err.message); }
                }
                if(e.key==="Escape") setAddingToCol(null);
              }}
              placeholder="Nombre completo..."
              style={{width:"100%",padding:"12px 14px",border:`1.5px solid ${T.border}`,borderRadius:12,fontSize:14,outline:"none",boxSizing:"border-box",fontFamily:"inherit",marginBottom:20}}
            />
            <div style={{display:"flex",gap:8}}>
              <button onClick={async()=>{
                if(!newClientName.trim()) return;
                try { await clientes.crear({ agencia_id: agencias.indexOf(activeAgency)+1, columna_id: addingToCol.id, nombre: newClientName.trim() }); recargar(); setAddingToCol(null); } catch(err){ alert(err.message); }
              }} style={{flex:1,padding:"11px",background:`linear-gradient(135deg,${T.primary},${T.sidebarBot})`,color:"#fff",border:"none",borderRadius:12,fontSize:13,fontWeight:700,cursor:"pointer"}}>Añadir</button>
              <button onClick={()=>setAddingToCol(null)} style={{flex:1,padding:"11px",background:T.panel,color:T.textSub,border:"none",borderRadius:12,fontSize:13,cursor:"pointer"}}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MODAL DETALLE CLIENTE ─────────────────────────────────────────────────────

// Pestañas por rol:
// Principal → todos
// Estadísticas → admin, manager, supervisor
// Automatización → admin, manager

function ClienteModal({ card, user, onClose }) {
  const { data: cliente, loading, recargar: recargarCliente } = useFetch(() => clientes.obtener(card.id), [card.id]);
  const { data: msgs, loading: loadingMsgs, recargar: recargarMsgs } = useFetch(() => mensajes.listar(card.id), [card.id]);
  const { data: allCampos } = useFetch(() => campos.listar());
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [savingField, setSavingField] = useState(false);
  const [activeTab, setActiveTab] = useState("principal");

  const isAdmin = user.rol === "admin";
  const isManager = user.rol === "manager";
  const isSupervisor = user.rol === "supervisor";

  // Pestañas disponibles según rol
  const tabs = [
    { id:"principal", label:"Principal", roles:["admin","manager","supervisor","agente"] },
    { id:"estadisticas", label:"Estadísticas", roles:["admin","manager","supervisor"] },
    { id:"automatizacion", label:"Automatización", roles:["admin","manager"] },
  ].filter(t => t.roles.includes(user.rol));

  // Campos separados por pestaña
  // Los campos con pestaña guardada en meta.pestaña, o "principal" por defecto
  const getTab = (campo_id) => {
    const meta = (allCampos||[]).find(f=>f.id===campo_id);
    return meta?.pestaña || "principal";
  };

  const camposTodos = (cliente?.campos || []).filter(c => {
    if (isAdmin) return true;
    const meta = (allCampos||[]).find(f => f.id === c.campo_id);
    return meta?.visible_agentes !== false;
  });

  const camposTab = camposTodos.filter(c => getTab(c.campo_id) === activeTab);

  // Campos de automatización - solo los técnicos (visible_agentes=false)
  const camposAuto = (cliente?.campos || []).filter(c => {
    const meta = (allCampos||[]).find(f=>f.id===c.campo_id);
    return meta?.visible_agentes === false || getTab(c.campo_id) === "automatizacion";
  });

  const handleSend = async () => {
    if (!newMsg.trim() || sending) return;
    setSending(true);
    try {
      await mensajes.enviar(card.id, newMsg);
      setNewMsg(""); recargarMsgs();
    } catch (err) { alert(err.message); }
    finally { setSending(false); }
  };

  const handleSaveField = async () => {
    if (!editingField) return;
    setSavingField(true);
    try {
      await clientes.actualizar(card.id, { campos: [{ campo_id: editingField.campo_id, valor: editingField.valor }] });
      recargarCliente(); setEditingField(null);
    } catch(err) { alert(err.message); }
    finally { setSavingField(false); }
  };

  const renderField = (c) => {
    const isEditing = editingField?.campo_id === c.campo_id;
    const meta = (allCampos||[]).find(f=>f.id===c.campo_id);
    const canEdit = meta?.visible_agentes !== false || isAdmin || isManager;
    return (
      <div key={c.campo_id} style={{ marginBottom:8, background:"#fff", borderRadius:10, border:`1px solid ${T.border}`, overflow:"hidden" }}>
        <div style={{ padding:"6px 10px 2px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontSize:10, fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:0.4 }}>{c.label}</span>
          {canEdit && !isEditing && (
            <button onClick={()=>setEditingField({campo_id:c.campo_id, valor:c.valor||""})}
              style={{fontSize:10,color:T.primary,background:"none",border:"none",cursor:"pointer",fontWeight:700,padding:"0 4px"}}>✏</button>
          )}
        </div>
        {isEditing ? (
          <div style={{padding:"4px 10px 10px"}}>
            <input autoFocus value={editingField.valor} onChange={e=>setEditingField(p=>({...p,valor:e.target.value}))}
              onKeyDown={e=>{if(e.key==="Enter")handleSaveField();if(e.key==="Escape")setEditingField(null);}}
              style={{width:"100%",padding:"6px 8px",border:`1.5px solid ${T.primary}`,borderRadius:6,fontSize:12,outline:"none",boxSizing:"border-box",fontFamily:"inherit",marginBottom:6}} />
            <div style={{display:"flex",gap:4}}>
              <button onClick={handleSaveField} disabled={savingField} style={{flex:1,padding:"5px",background:T.primary,color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer"}}>{savingField?"...":"Guardar"}</button>
              <button onClick={()=>setEditingField(null)} style={{flex:1,padding:"5px",background:T.panel,color:T.textSub,border:"none",borderRadius:6,fontSize:11,cursor:"pointer"}}>Cancelar</button>
            </div>
          </div>
        ) : (
          <p style={{ margin:0, padding:"2px 10px 8px", fontSize:13, color: c.valor ? T.textPrimary : T.textMuted, fontWeight: c.valor ? 500 : 400 }}>{c.valor || "—"}</p>
        )}
      </div>
    );
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(28,25,23,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(5px)" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 20, width: "92vw", maxWidth: 980, height: "84vh", display: "flex", overflow: "hidden", boxShadow: "0 28px 70px rgba(0,0,0,0.25)", border:`1px solid ${T.border}` }}>

        {/* Ficha izquierda */}
        <div style={{ width: 320, flexShrink: 0, borderRight: `1px solid ${T.border}`, display:"flex", flexDirection:"column", background: T.panel }}>

          {/* Header */}
          <div style={{ padding:"14px 18px", background:"#fff", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
            <div>
              <p style={{ margin:0, fontWeight:800, fontSize:15, color:T.textPrimary }}>{card.nombre || "Sin nombre"}</p>
              <p style={{ margin:"2px 0 0", fontSize:11, color:T.textMuted }}>#{card.id} · {card.canal || "—"}</p>
            </div>
            <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:22, color:T.textMuted, lineHeight:1 }}>×</button>
          </div>

          {/* Tabs */}
          <div style={{ display:"flex", background:"#fff", borderBottom:`1px solid ${T.border}`, flexShrink:0 }}>
            {tabs.map(tab => (
              <button key={tab.id} onClick={()=>setActiveTab(tab.id)}
                style={{ flex:1, padding:"9px 4px", border:"none", background:"transparent", fontSize:11, fontWeight: activeTab===tab.id?700:500,
                  color: activeTab===tab.id?T.primary:T.textSub, borderBottom: activeTab===tab.id?`2.5px solid ${T.primary}`:"2.5px solid transparent", cursor:"pointer", transition:"all 0.15s" }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content area */}
          <div style={{ flex:1, overflowY:"auto", padding:"12px 14px" }}>
            {loading ? <Spinner /> : (
              <>
                {activeTab === "principal" && (
                  camposTab.length === 0
                    ? <p style={{fontSize:13,color:T.textMuted,textAlign:"center",padding:"20px 0"}}>Sin campos en esta pestaña</p>
                    : camposTab.map(renderField)
                )}
                {activeTab === "estadisticas" && (
                  <div>
                    <p style={{fontSize:11,color:T.textMuted,margin:"0 0 12px",fontStyle:"italic"}}>Estadísticas del lead</p>
                    {[
                      { label:"Columna actual", value: card.columna || "—" },
                      { label:"Canal", value: card.canal || "—" },
                      { label:"Último mensaje", value: card.ultimo_mensaje ? new Date(card.ultimo_mensaje).toLocaleString("es-MX") : "—" },
                      { label:"Mensajes sin leer", value: card.no_leidos || 0 },
                      { label:"Creado", value: cliente?.creado_en ? new Date(cliente.creado_en).toLocaleDateString("es-MX") : "—" },
                    ].map(row => (
                      <div key={row.label} style={{ marginBottom:8, padding:"8px 10px", background:"#fff", borderRadius:10, border:`1px solid ${T.border}` }}>
                        <p style={{margin:"0 0 2px",fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:0.4}}>{row.label}</p>
                        <p style={{margin:0,fontSize:13,color:T.textPrimary,fontWeight:500}}>{String(row.value)}</p>
                      </div>
                    ))}
                    {camposTab.length > 0 && <div style={{marginTop:12}}>{camposTab.map(renderField)}</div>}
                  </div>
                )}
                {activeTab === "automatizacion" && (
                  <div>
                    <p style={{fontSize:11,color:T.textMuted,margin:"0 0 12px",fontStyle:"italic"}}>Campos técnicos — actualizados por n8n</p>
                    {camposAuto.length === 0
                      ? <p style={{fontSize:13,color:T.textMuted,textAlign:"center",padding:"20px 0"}}>Sin campos de automatización</p>
                      : camposAuto.map(renderField)
                    }
                    {camposTab.filter(c=>!camposAuto.find(a=>a.campo_id===c.campo_id)).map(renderField)}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Chat derecha */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, fontWeight: 700, fontSize: 14, color: T.textPrimary, background:"#fff", display:"flex", alignItems:"center", gap:8 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            Chat — {card.nombre}
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 10, background: T.bg }}>
            {loadingMsgs ? <Spinner /> : (msgs || []).map(msg => {
              const isOut = msg.origen === "agente" || msg.origen === "bot";
              return (
                <div key={msg.id} style={{ display: "flex", justifyContent: isOut?"flex-end":"flex-start" }}>
                  <div style={{ maxWidth: "65%", background: isOut?"#dcf8c6":"#fff", borderRadius: isOut?"16px 4px 16px 16px":"4px 16px 16px 16px", padding: "10px 14px", boxShadow: "0 1px 3px rgba(0,0,0,0.07)", fontSize: 13, color: T.textPrimary, lineHeight: 1.5, border:`1px solid ${T.border}` }}>
                    {msg.texto}
                    <p style={{ margin: "4px 0 0", fontSize: 10, color: T.textMuted, textAlign: isOut?"right":"left" }}>
                      {new Date(msg.created_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ padding: "12px 16px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 10, background:"#fff" }}>
            <textarea value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => { if (e.key==="Enter"&&!e.shiftKey) { e.preventDefault(); handleSend(); } }} placeholder="Escribe un mensaje..." rows={1} style={{ flex: 1, padding: "10px 14px", borderRadius: 12, border: `1px solid ${T.border}`, fontSize: 13, resize: "none", outline: "none", fontFamily: "inherit", background: T.panel }} />
            <button onClick={handleSend} disabled={sending} style={{ background: "#25d366", border: "none", borderRadius: 12, padding: "10px 16px", cursor: "pointer", fontSize: 16 }}>➤</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ESTADÍSTICAS ─────────────────────────────────────────────────────────────

// Horario de oficina: L-V 9-18h, S 10-14h
function isOfficeHours(date) {
  const d = new Date(date);
  const day = d.getDay(); // 0=dom,1=lun,...,6=sab
  const hour = d.getHours();
  if (day === 0) return false; // domingo cerrado
  if (day === 6) return hour >= 10 && hour < 14; // sábado 10-14
  return hour >= 9 && hour < 18; // L-V 9-18
}

function officeMinutesBetween(start, end) {
  let minutes = 0;
  let current = new Date(start);
  while (current < end) {
    if (isOfficeHours(current)) minutes++;
    current = new Date(current.getTime() + 60000);
  }
  return minutes;
}

function fmtMinutes(m) {
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.round(m/60)}h ${m%60}m`;
  return `${Math.round(m/1440)}d ${Math.round((m%1440)/60)}h`;
}

// Datos de ejemplo — se reemplazarán con API real
const STATS_DATA = {
  // Volumen leads
  leads: {
    today:       { count: 12, prev: 10 },
    week:        { count: 67, prev: 58 },
    month:       { count: 243, prev: 211 },
  },
  byAgency: [
    { name: "Independencia", today: 6,  week: 31, month: 118 },
    { name: "Periodismo",    today: 4,  week: 22, month: 84  },
    { name: "Centro",        today: 2,  week: 14, month: 41  },
  ],
  byCanal: [
    { canal: "WhatsApp",  icon: "💬", color: "#25d366", count: 142, prev: 128 },
    { canal: "Instagram", icon: "📷", color: "#e1306c", count: 67,  prev: 71  },
    { canal: "Messenger", icon: "💙", color: "#0084ff", count: 34,  prev: 29  },
  ],
  // Velocidad
  firstResponseMin: { avg: 14, prev: 18 }, // minutos de oficina
  saleTimeMin:      { avg: 4320, prev: 5040 }, // minutos = 3 días
  inactiveLeads:    8,
  // Por agente
  agents: [
    { name: "Laura Santos",   agency: "Independencia", id: 5, sales: { day:2, week:8,  month:31 }, prevSales: { day:1, week:6, month:28 }, leads: { day:5, week:22, month:84 }, firstResponseMin: 11, saleTimeMin: 3960 },
    { name: "Judith Maciel",  agency: "Periodismo",    id: 4, sales: { day:1, week:6,  month:28 }, prevSales: { day:2, week:7, month:25 }, leads: { day:4, week:18, month:71 }, firstResponseMin: 18, saleTimeMin: 4680 },
    { name: "Héctor Fernando",agency: "Centro",         id: 6, sales: { day:1, week:5,  month:22 }, prevSales: { day:0, week:4, month:19 }, leads: { day:3, week:14, month:52 }, firstResponseMin: 22, saleTimeMin: 5400 },
  ],
  // Destinos
  destinations: [
    { dest: "Ixtapa / Zihuatanejo", count: 89, prev: 76 },
    { dest: "Cancún",               count: 54, prev: 58 },
    { dest: "Riviera Maya",          count: 38, prev: 31 },
    { dest: "Los Cabos",             count: 21, prev: 18 },
    { dest: "Puerto Vallarta",       count: 11, prev: 14 },
  ],
  // Seguimientos
  followups: { done: 34, effective: 21, pending: 8, prev: { done: 28, effective: 16 } },
  // Conversión
  conversion: [
    { name: "Independencia", total: 118, sold: 33 },
    { name: "Periodismo",    total: 84,  sold: 28 },
    { name: "Centro",        total: 41,  sold: 17 },
  ],
};

// Permisos por defecto — se pueden cambiar desde Configuración
const DEFAULT_STAT_VISIBILITY = {
  volumen:           { admin:true, manager:true, supervisor:true, agente:false },
  velocidad_global:  { admin:true, manager:true, supervisor:true, agente:false },
  velocidad_propia:  { admin:true, manager:true, supervisor:true, agente:true  },
  inactivos:         { admin:true, manager:true, supervisor:true, agente:true  },
  ranking:           { admin:true, manager:true, supervisor:true, agente:true  },
  leads_agente:      { admin:true, manager:true, supervisor:true, agente:true  },
  destinos:          { admin:true, manager:true, supervisor:true, agente:false },
  seguimientos:      { admin:true, manager:true, supervisor:true, agente:true  },
  conversion:        { admin:true, manager:true, supervisor:true, agente:false },
};

function pct(current, prev) {
  if (!prev) return null;
  return Math.round(((current - prev) / prev) * 100);
}

function PctBadge({ current, prev }) {
  const p = pct(current, prev);
  if (p === null) return null;
  const up = p >= 0;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: up ? "#059669" : "#ef4444", background: up ? "#dcfce7" : "#fee2e2", padding: "2px 7px", borderRadius: 20, marginLeft: 8 }}>
      {up ? "▲" : "▼"} {Math.abs(p)}%
    </span>
  );
}

function KPICard({ title, value, prev, sub, icon, color = "#6366f1" }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "18px 20px", flex: 1, minWidth: 140 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>{title}</span>
        <span style={{ fontSize: 20 }}>{icon}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
        <p style={{ margin: 0, fontSize: 28, fontWeight: 900, color, letterSpacing: -1 }}>{value}</p>
        {prev !== undefined && <PctBadge current={typeof value === "string" ? parseFloat(value) : value} prev={prev} />}
      </div>
      {sub && <p style={{ margin: 0, fontSize: 11, color: "#94a3b8" }}>{sub}</p>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "20px 24px", marginBottom: 16 }}>
      <h3 style={{ margin: "0 0 16px", fontSize: 13, fontWeight: 800, color: "#1e293b" }}>{title}</h3>
      {children}
    </div>
  );
}

function BarRow({ label, value, maxVal, color, suffix = "", extra }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
      <span style={{ fontSize: 12, color: "#475569", width: 160, flexShrink: 0, textAlign: "right" }}>{label}</span>
      <div style={{ flex: 1, height: 22, background: "#f1f5f9", borderRadius: 6, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${Math.min((value/maxVal)*100, 100)}%`, background: color || "#6366f1", borderRadius: 6, display: "flex", alignItems: "center", paddingLeft: 8, transition: "width 0.5s ease" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", whiteSpace: "nowrap" }}>{value}{suffix}</span>
        </div>
      </div>
      {extra}
    </div>
  );
}

function StatsView({ user }) {
  const [period, setPeriod]   = useState("week");
  const [agentPeriod, setAgentPeriod] = useState("week");
  const rol = user?.rol || "admin";
  const vis = DEFAULT_STAT_VISIBILITY;
  const canSee = (key) => vis[key]?.[rol] ?? false;

  // Para agentes: filtrar solo sus datos
  const myAgent = STATS_DATA.agents.find(a => a.id === user?.id);
  const agentsToShow = (rol === "agente") ? (myAgent ? [myAgent] : []) : STATS_DATA.agents;

  const periodLabel = { today: "Hoy vs ayer", week: "Esta semana vs anterior", month: "Este mes vs anterior" };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 28, background: "#f1f5f9" }}>
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: "#1e293b" }}>📈 Estadísticas</h2>
          <div style={{ display: "flex", gap: 6, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 4 }}>
            {[["today","Hoy"],["week","Semana"],["month","Mes"]].map(([p,l]) => (
              <button key={p} onClick={() => setPeriod(p)} style={{ padding: "6px 14px", border: "none", borderRadius: 7, background: period===p?"#6366f1":"transparent", color: period===p?"#fff":"#64748b", fontSize: 12, cursor: "pointer", fontWeight: 600, transition: "all 0.15s" }}>{l}</button>
            ))}
          </div>
        </div>

        {/* ── VOLUMEN DE LEADS ── */}
        {canSee("volumen") && (
          <>
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <KPICard title="Leads recibidos" value={STATS_DATA.leads[period].count} prev={STATS_DATA.leads[period].prev} sub={periodLabel[period]} color="#6366f1" icon="📥" />
              {STATS_DATA.byAgency.map(a => (
                <KPICard key={a.name} title={a.name} value={a[period]} prev={Math.round(a[period]*0.88)} sub="leads" color="#3b82f6" icon="🏢" />
              ))}
            </div>

            <Section title="Leads por canal de entrada">
              <div style={{ display: "flex", gap: 12 }}>
                {STATS_DATA.byCanal.map(c => (
                  <div key={c.canal} style={{ flex: 1, textAlign: "center", padding: "16px 8px", background: c.color+"10", borderRadius: 12, border: `1px solid ${c.color}22` }}>
                    <p style={{ margin: "0 0 6px", fontSize: 24 }}>{c.icon}</p>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <p style={{ margin: 0, fontSize: 22, fontWeight: 900, color: c.color }}>{c.count}</p>
                      <PctBadge current={c.count} prev={c.prev} />
                    </div>
                    <p style={{ margin: "4px 0 0", fontSize: 11, color: "#64748b", fontWeight: 600 }}>{c.canal}</p>
                  </div>
                ))}
              </div>
            </Section>
          </>
        )}

        {/* ── VELOCIDAD GLOBAL ── */}
        {canSee("velocidad_global") && (
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <KPICard title="Tiempo 1ª respuesta" value={fmtMinutes(STATS_DATA.firstResponseMin.avg)} prev={STATS_DATA.firstResponseMin.prev} sub="Horas de oficina · media del equipo" color="#f59e0b" icon="⚡" />
            <KPICard title="Tiempo medio de venta" value={fmtMinutes(STATS_DATA.saleTimeMin.avg)} prev={STATS_DATA.saleTimeMin.prev} sub="Desde columna propia hasta Vendido" color="#8b5cf6" icon="🏆" />
            <KPICard title="Leads sin actividad +24h" value={STATS_DATA.inactiveLeads} sub="Clientes sin respuesta reciente" color="#ef4444" icon="⚠️" />
          </div>
        )}

        {/* ── RANKING Y RENDIMIENTO POR AGENTE ── */}
        {(canSee("ranking") || canSee("velocidad_propia") || canSee("leads_agente")) && (
          <Section title={`${rol === "agente" ? "Mis estadísticas" : "Rendimiento por agente"}`}>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginBottom: 16 }}>
              {[["day","Hoy"],["week","Semana"],["month","Mes"]].map(([p,l]) => (
                <button key={p} onClick={() => setAgentPeriod(p)} style={{ padding: "5px 12px", border: "1px solid", borderColor: agentPeriod===p?"#6366f1":"#e2e8f0", background: agentPeriod===p?"#eef2ff":"transparent", color: agentPeriod===p?"#6366f1":"#64748b", borderRadius: 8, fontSize: 11, cursor: "pointer", fontWeight: 600 }}>{l}</button>
              ))}
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #f1f5f9" }}>
                    <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Agente</th>
                    {canSee("ranking")      && <th style={{ textAlign: "center", padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>🏆 Ventas</th>}
                    {canSee("leads_agente") && <th style={{ textAlign: "center", padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>📥 Leads</th>}
                    {canSee("velocidad_propia") && <th style={{ textAlign: "center", padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>⚡ 1ª Respuesta</th>}
                    {canSee("velocidad_propia") && <th style={{ textAlign: "center", padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>⏱ T. Venta</th>}
                  </tr>
                </thead>
                <tbody>
                  {agentsToShow.sort((a,b) => b.sales[agentPeriod] - a.sales[agentPeriod]).map((agent, i) => (
                    <tr key={agent.id} style={{ borderBottom: "1px solid #f1f5f9", background: i===0&&rol!=="agente"?"#fefce8":"transparent" }}>
                      <td style={{ padding: "12px 12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          {rol !== "agente" && <span style={{ fontSize: 16 }}>{i===0?"🥇":i===1?"🥈":"🥉"}</span>}
                          <div>
                            <p style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}>{agent.name}</p>
                            <p style={{ margin: 0, fontSize: 11, color: "#94a3b8" }}>{agent.agency}</p>
                          </div>
                        </div>
                      </td>
                      {canSee("ranking") && (
                        <td style={{ textAlign: "center", padding: "12px" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                            <span style={{ fontSize: 16, fontWeight: 900, color: "#6366f1" }}>{agent.sales[agentPeriod]}</span>
                            <PctBadge current={agent.sales[agentPeriod]} prev={agent.prevSales[agentPeriod]} />
                          </div>
                        </td>
                      )}
                      {canSee("leads_agente") && (
                        <td style={{ textAlign: "center", padding: "12px" }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: "#3b82f6" }}>{agent.leads[agentPeriod]}</span>
                        </td>
                      )}
                      {canSee("velocidad_propia") && (
                        <td style={{ textAlign: "center", padding: "12px" }}>
                          <span style={{ fontSize: 13, color: agent.firstResponseMin <= 15 ? "#059669" : agent.firstResponseMin <= 30 ? "#f59e0b" : "#ef4444", fontWeight: 700 }}>
                            {fmtMinutes(agent.firstResponseMin)}
                          </span>
                        </td>
                      )}
                      {canSee("velocidad_propia") && (
                        <td style={{ textAlign: "center", padding: "12px" }}>
                          <span style={{ fontSize: 13, color: "#475569", fontWeight: 600 }}>{fmtMinutes(agent.saleTimeMin)}</span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* ── LEADS SIN ACTIVIDAD ── */}
        {canSee("inactivos") && (
          <div style={{ background: STATS_DATA.inactiveLeads > 5 ? "#fef2f2" : "#fff", border: `1px solid ${STATS_DATA.inactiveLeads > 5 ? "#fecaca" : "#e2e8f0"}`, borderRadius: 14, padding: "16px 24px", marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
            <span style={{ fontSize: 32 }}>{STATS_DATA.inactiveLeads > 5 ? "🚨" : "✅"}</span>
            <div>
              <p style={{ margin: 0, fontWeight: 800, fontSize: 15, color: STATS_DATA.inactiveLeads > 5 ? "#ef4444" : "#1e293b" }}>
                {STATS_DATA.inactiveLeads} leads sin actividad en más de 24h
              </p>
              <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>Clientes que no han recibido respuesta en el último día laborable</p>
            </div>
          </div>
        )}

        {/* ── DESTINOS ── */}
        {canSee("destinos") && (
          <Section title="Destinos más solicitados">
            {STATS_DATA.destinations.map((d, i) => (
              <BarRow key={i} label={d.dest} value={d.count} maxVal={STATS_DATA.destinations[0].count} color={["#6366f1","#3b82f6","#8b5cf6","#10b981","#f59e0b"][i]}
                extra={<PctBadge current={d.count} prev={d.prev} />}
              />
            ))}
          </Section>
        )}

        {/* ── SEGUIMIENTOS ── */}
        {canSee("seguimientos") && (
          <Section title="Seguimientos">
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <KPICard title="Realizados" value={STATS_DATA.followups.done} prev={STATS_DATA.followups.prev.done} sub="seguimientos enviados" color="#6366f1" icon="🔔" />
              <KPICard title="Efectivos" value={STATS_DATA.followups.effective} prev={STATS_DATA.followups.prev.effective} sub="cliente respondió y avanzó" color="#10b981" icon="✅" />
              <KPICard title="Pendientes hoy" value={STATS_DATA.followups.pending} sub="por realizar" color="#f59e0b" icon="⏳" />
            </div>
            <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: "#64748b" }}>Tasa de efectividad</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: "#10b981" }}>
                  {Math.round((STATS_DATA.followups.effective / STATS_DATA.followups.done) * 100)}%
                </span>
              </div>
              <div style={{ height: 10, background: "#e2e8f0", borderRadius: 6, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.round((STATS_DATA.followups.effective / STATS_DATA.followups.done) * 100)}%`, background: "linear-gradient(90deg, #6366f1, #10b981)", borderRadius: 6 }} />
              </div>
            </div>
          </Section>
        )}

        {/* ── CONVERSIÓN ── */}
        {canSee("conversion") && (
          <Section title="Tasa de conversión por agencia">
            {STATS_DATA.conversion.map((a, i) => {
              const pctVal = Math.round((a.sold/a.total)*100);
              return (
                <div key={i} style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 13, color: "#475569" }}>{a.name}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>{a.sold} vendidos / {a.total} leads</span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: ["#6366f1","#10b981","#f59e0b"][i] }}>{pctVal}%</span>
                    </div>
                  </div>
                  <div style={{ height: 10, background: "#f1f5f9", borderRadius: 6, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pctVal}%`, background: ["#6366f1","#10b981","#f59e0b"][i], borderRadius: 6 }} />
                  </div>
                </div>
              );
            })}
          </Section>
        )}

      </div>
    </div>
  );
}


// ─── CHATS ────────────────────────────────────────────────────────────────────

const MOCK_CHATS_DATA = [
  { id: 1, name: "Cristian López",   phone: "+5214431198008",   agency: "Rs Viajes Independencia", agentId: 5, columnId: 3001004, columnTitle: "Cualificado Urgente", columnColor: "#ef4444", columnType: "assigned", lastMsg: "Hola, quisiera información sobre Ixtapa",       time: "2026-04-27T22:57", unread: 3, destination: "Ixtapa",             fechas: "24-26 abril",    adultos: 4, ninos: 0, canal: "whatsapp",  visibleToAll: false, tagged: false },
  { id: 2, name: "Chony Argote",     phone: "+5214437294436",   agency: "Rs Viajes Independencia", agentId: 5, columnId: 3001005, columnTitle: "Cualificados",        columnColor: "#8b5cf6", columnType: "assigned", lastMsg: "¿Cuánto costaría el paquete familiar?",       time: "2026-04-27T21:30", unread: 1, destination: "Ixtapa Zihuatanejo", fechas: "28 agosto",      adultos: 2, ninos: 2, canal: "instagram",  visibleToAll: true,  tagged: false },
  { id: 3, name: "Dulce Linares",    phone: null,               agency: "Rs Viajes Independencia", agentId: 5, columnId: 3001004, columnTitle: "Cualificado Urgente", columnColor: "#ef4444", columnType: "assigned", lastMsg: "Buenos días, vi su anuncio en Instagram",      time: "2026-04-27T19:45", unread: 5, destination: "Ixtapa",             fechas: "temporada baja", adultos: 2, ninos: 0, canal: "instagram",  visibleToAll: false, tagged: true  },
  { id: 4, name: "Fher Anguiano",    phone: null,               agency: "Rs Viajes Independencia", agentId: 5, columnId: 3001005, columnTitle: "Cualificados",        columnColor: "#8b5cf6", columnType: "assigned", lastMsg: "¿Tienen disponibilidad este jueves?",          time: "2026-04-27T15:20", unread: 1, destination: "Ixtapa",             fechas: "jueves-domingo", adultos: 3, ninos: 1, canal: "whatsapp",  visibleToAll: true,  tagged: false },
  { id: 5, name: "Ana González",     phone: "+52 555 100 2000", agency: "Rs Viajes Periodismo",   agentId: 4, columnId: 3001010, columnTitle: "Cualificados",        columnColor: "#8b5cf6", columnType: "assigned", lastMsg: "Perfecto, entonces reservamos para junio",     time: "2026-04-27T18:30", unread: 0, destination: "Los Cabos",           fechas: "junio 2026",     adultos: 2, ninos: 0, canal: "messenger",  visibleToAll: false, tagged: false },
  { id: 6, name: "Roberto Cruz",     phone: "+52 555 300 4000", agency: "Rs Viajes Periodismo",   agentId: 4, columnId: 3001010, columnTitle: "Cualificados",        columnColor: "#8b5cf6", columnType: "assigned", lastMsg: "¿Incluye el vuelo desde Guadalajara?",         time: "2026-04-27T17:10", unread: 2, destination: "Cancún",             fechas: "mayo 2026",      adultos: 4, ninos: 2, canal: "whatsapp",  visibleToAll: true,  tagged: false },
];

const CANAL_ICONS  = { whatsapp: "💬", instagram: "📷", messenger: "💙" };
const CANAL_COLORS = { whatsapp: "#25d366", instagram: "#e1306c", messenger: "#0084ff" };

const MSGS_MAP = {
  1: [
    { id: 1, from: "bot",    name: "SalesBot", time: "22:50", text: "Bienvenido a Rs Viajes 😊 ¿Para cuántas personas sería el viaje?" },
    { id: 2, from: "client", name: "Cliente",  time: "22:53", text: "Somos 4 adultos" },
    { id: 3, from: "bot",    name: "SalesBot", time: "22:54", text: "¡Perfecto! ¿Y qué fechas tienen en mente?" },
    { id: 4, from: "client", name: "Cliente",  time: "22:57", text: "Hola, quisiera información sobre Ixtapa" },
  ],
  2: [
    { id: 1, from: "client", name: "Cliente", time: "21:00", text: "Buenos días, me interesa un paquete a Zihuatanejo" },
    { id: 2, from: "agent",  name: "Laura",   time: "21:05", text: "Hola! Con gusto le ayudo. ¿Para cuándo sería el viaje?" },
    { id: 3, from: "client", name: "Cliente", time: "21:30", text: "¿Cuánto costaría el paquete familiar?" },
  ],
};

function FichaModal({ chat, onClose, user }) {
  const [msgs, setMsgs] = useState(MSGS_MAP[chat.id] || [{ id: 1, from: "client", name: "Cliente", time: "—", text: chat.lastMsg }]);
  const [msg, setMsg]   = useState("");
  const fields = [
    { label: "Destino",  value: chat.destination },
    { label: "Fechas",   value: chat.fechas },
    { label: "Adultos",  value: chat.adultos },
    { label: "Niños",    value: chat.ninos },
    { label: "Teléfono", value: chat.phone || "—" },
    { label: "Canal",    value: chat.canal },
    { label: "Columna",  value: chat.columnTitle },
    { label: "Agencia",  value: chat.agency.replace("Rs Viajes ","") },
  ];
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.55)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, backdropFilter:"blur(4px)" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"#fff", borderRadius:16, width:"88vw", maxWidth:860, height:"80vh", display:"flex", overflow:"hidden", boxShadow:"0 25px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ width:270, flexShrink:0, borderRight:"1px solid #e2e8f0", display:"flex", flexDirection:"column", background:"#1e293b" }}>
          <div style={{ padding:"14px 16px", borderBottom:"1px solid #334155", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:36, height:36, borderRadius:"50%", background:chat.columnColor+"33", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800, color:chat.columnColor }}>{chat.name.split(" ").map(n=>n[0]).join("").slice(0,2)}</div>
              <div><p style={{ margin:0, fontWeight:800, color:"#f8fafc", fontSize:14 }}>{chat.name}</p><p style={{ margin:0, fontSize:10, color:"#64748b" }}>{chat.phone||"Sin teléfono"}</p></div>
            </div>
            <button onClick={onClose} style={{ background:"none", border:"none", color:"#64748b", fontSize:20, cursor:"pointer" }}>×</button>
          </div>
          <div style={{ flex:1, overflowY:"auto", background:"#fff" }}>
            {fields.map(f=>(
              <div key={f.label} style={{ padding:"8px 16px", borderBottom:"1px solid #f1f5f9", display:"flex", gap:8 }}>
                <span style={{ fontSize:11, color:"#64748b", width:80, flexShrink:0 }}>{f.label}</span>
                <span style={{ fontSize:12, color:"#1e293b", fontWeight:600 }}>{f.value ?? "—"}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ flex:1, display:"flex", flexDirection:"column", background:"#f8fafc" }}>
          <div style={{ padding:"12px 20px", borderBottom:"1px solid #e2e8f0", background:"#fff", display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:18 }}>{CANAL_ICONS[chat.canal]}</span>
            <div><p style={{ margin:0, fontWeight:700, fontSize:14, color:"#1e293b" }}>{chat.name}</p><p style={{ margin:0, fontSize:11, color:"#10b981" }}>En línea · {chat.canal}</p></div>
          </div>
          <div style={{ flex:1, overflowY:"auto", padding:"16px 20px", display:"flex", flexDirection:"column", gap:10 }}>
            {msgs.map(m=>{
              const isOut = m.from==="agent"||m.from==="bot";
              return (
                <div key={m.id} style={{ display:"flex", justifyContent:isOut?"flex-end":"flex-start" }}>
                  <div style={{ maxWidth:"65%", background:isOut?"#dcf8c6":"#fff", borderRadius:isOut?"16px 4px 16px 16px":"4px 16px 16px 16px", padding:"10px 14px", boxShadow:"0 1px 2px rgba(0,0,0,0.08)", fontSize:13, color:"#1e293b", lineHeight:1.5 }}>
                    {m.text}<p style={{ margin:"4px 0 0", fontSize:10, color:"#94a3b8", textAlign:isOut?"right":"left" }}>{m.time}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ padding:"12px 16px", borderTop:"1px solid #e2e8f0", background:"#fff", display:"flex", gap:8 }}>
            <input value={msg} onChange={e=>setMsg(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&msg.trim()){setMsgs(prev=>[...prev,{id:Date.now(),from:"agent",name:user.nombre,time:new Date().toLocaleTimeString("es-MX",{hour:"2-digit",minute:"2-digit"}),text:msg}]);setMsg("");}}} placeholder="Escribe un mensaje..." style={{ flex:1, padding:"10px 14px", borderRadius:12, border:"1px solid #e2e8f0", fontSize:13, outline:"none", fontFamily:"inherit" }} />
            <button onClick={()=>{if(msg.trim()){setMsgs(prev=>[...prev,{id:Date.now(),from:"agent",name:user.nombre,time:new Date().toLocaleTimeString("es-MX",{hour:"2-digit",minute:"2-digit"}),text:msg}]);setMsg("");}}} style={{ background:"#25d366", border:"none", borderRadius:10, padding:"10px 16px", cursor:"pointer", fontSize:16 }}>➤</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatsView({ user }) {
  const [chatList, setChatList]     = useState(MOCK_CHATS_DATA);
  const [selected, setSelected]     = useState(null);
  const [messages, setMessages]     = useState([]);
  const [newMsg, setNewMsg]         = useState("");
  const [search, setSearch]         = useState("");
  const [filter, setFilter]         = useState("todos");
  const [showFicha, setShowFicha]   = useState(false);
  const chatEndRef  = useRef(null);
  const fileInputRef = useRef(null);

  const visible = chatList.filter(c => {
    if (c.columnType !== "assigned") return false;
    const r = user.rol;
    if (r === "admin")   return true;
    if (r === "manager" || r === "supervisor") return c.visibleToAll;
    if (r === "agente")  return c.agentId === user.id || (c.agency === user.agencia && c.visibleToAll);
    return false;
  }).filter(c => {
    const ms = c.name.toLowerCase().includes(search.toLowerCase());
    const mf = filter==="todos" || (filter==="no_leidos"&&c.unread>0) || (filter==="pendientes"&&c.tagged);
    return ms && mf;
  }).sort((a,b)=>new Date(b.time)-new Date(a.time));

  const selectChat = (chat) => {
    setSelected(chat);
    setChatList(prev=>prev.map(c=>c.id===chat.id?{...c,unread:0}:c));
    setMessages(MSGS_MAP[chat.id]||[{id:1,from:"client",name:"Cliente",time:"—",text:chat.lastMsg}]);
  };

  const handleSend = () => {
    if (!newMsg.trim()) return;
    setMessages(prev=>[...prev,{id:Date.now(),from:"agent",name:user.nombre,time:new Date().toLocaleTimeString("es-MX",{hour:"2-digit",minute:"2-digit"}),text:newMsg}]);
    setNewMsg("");
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/") && file.type!=="application/pdf") { alert("Solo imágenes y PDFs"); return; }
    setMessages(prev=>[...prev,{id:Date.now(),from:"agent",name:user.nombre,time:new Date().toLocaleTimeString("es-MX",{hour:"2-digit",minute:"2-digit"}),text:null,file:{name:file.name,type:file.type,url:URL.createObjectURL(file)}}]);
    e.target.value="";
  };

  useEffect(()=>{chatEndRef.current?.scrollIntoView({behavior:"smooth"});},[messages]);

  const fmtTime = (iso) => {
    const d=new Date(iso), now=new Date();
    if(d.toDateString()===now.toDateString()) return d.toLocaleTimeString("es-MX",{hour:"2-digit",minute:"2-digit"});
    return d.toLocaleDateString("es-MX",{day:"2-digit",month:"2-digit"});
  };

  const totalUnread = chatList.reduce((s,c)=>s+(c.unread||0),0);

  return (
    <div style={{flex:1,display:"flex",overflow:"hidden"}}>
      {showFicha && selected && <FichaModal chat={selected} onClose={()=>setShowFicha(false)} user={user} />}

      {/* Lista */}
      <div style={{width:320,flexShrink:0,background:"#fff",borderRight:"1px solid #e2e8f0",display:"flex",flexDirection:"column"}}>
        <div style={{padding:"16px 16px 10px",borderBottom:"1px solid #f1f5f9"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <h2 style={{margin:0,fontSize:16,fontWeight:800,color:"#1e293b"}}>Chats</h2>
            {totalUnread>0 && <span style={{fontSize:11,padding:"2px 8px",borderRadius:20,background:"#ef444422",color:"#ef4444",fontWeight:700}}>{totalUnread} sin leer</span>}
          </div>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar cliente..." style={{width:"100%",padding:"8px 12px",border:"1px solid #e2e8f0",borderRadius:10,fontSize:13,outline:"none",boxSizing:"border-box",background:"#f8fafc",marginBottom:10}} />
          <div style={{display:"flex",gap:5}}>
            {[["todos","Todos"],["no_leidos","No leídos"],["pendientes","Pendientes"]].map(([val,lbl])=>(
              <button key={val} onClick={()=>setFilter(val)} style={{flex:1,padding:"5px 2px",border:"1px solid",borderColor:filter===val?"#6366f1":"#e2e8f0",background:filter===val?"#eef2ff":"transparent",color:filter===val?"#6366f1":"#64748b",borderRadius:8,fontSize:10,cursor:"pointer",fontWeight:600}}>{lbl}</button>
            ))}
          </div>
        </div>

        <div style={{flex:1,overflowY:"auto"}}>
          {visible.length===0 && <div style={{padding:24,textAlign:"center",color:"#94a3b8",fontSize:13}}>No hay chats disponibles</div>}
          {visible.map(chat=>{
            // Color de fondo según columna: muy tenue (08 = 5% opacity)
            const chatBg = selected?.id===chat.id ? chat.columnColor+"22" : chat.columnColor+"08";
            const chatBgHover = chat.columnColor+"14";
            return (
            <div key={chat.id} onClick={()=>selectChat(chat)}
              style={{padding:"12px 16px",borderBottom:`1px solid ${T.border}`,cursor:"pointer",background:chatBg,display:"flex",gap:10,alignItems:"flex-start",borderLeft:selected?.id===chat.id?`3px solid ${chat.columnColor}`:"3px solid transparent",transition:"all 0.1s"}}
              onMouseEnter={e=>{if(selected?.id!==chat.id)e.currentTarget.style.background=chatBgHover;}}
              onMouseLeave={e=>{if(selected?.id!==chat.id)e.currentTarget.style.background=chatBg;}}
            >
              <div style={{position:"relative",flexShrink:0}}>
                <div style={{width:42,height:42,borderRadius:"50%",background:chat.columnColor+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:800,color:chat.columnColor}}>
                  {chat.name.split(" ").map(n=>n[0]).join("").slice(0,2)}
                </div>
                <div style={{position:"absolute",bottom:-2,right:-2,width:16,height:16,borderRadius:"50%",background:CANAL_COLORS[chat.canal],display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,border:"1.5px solid #fff"}}>
                  {CANAL_ICONS[chat.canal]}
                </div>
                {chat.unread>0 && <span style={{position:"absolute",top:-3,right:-3,background:"#ef4444",color:"#fff",fontSize:9,fontWeight:800,borderRadius:10,padding:"1px 5px",minWidth:16,textAlign:"center"}}>{chat.unread}</span>}
              </div>

              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:5}}>
                    <span style={{fontSize:13,fontWeight:chat.unread>0?800:600,color:T.textPrimary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:120}}>{chat.name}</span>
                    {chat.tagged && <span style={{fontSize:9,padding:"1px 5px",borderRadius:10,background:"#fef3c7",color:"#92400e",fontWeight:700}}>⏳</span>}
                  </div>
                  <span style={{fontSize:10,color:T.textMuted,flexShrink:0}}>{fmtTime(chat.time)}</span>
                </div>
                <p style={{fontSize:11,color:T.textSub,margin:"0 0 5px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:chat.unread>0?600:400}}>{chat.lastMsg}</p>
                <div style={{display:"flex",gap:4,flexWrap:"wrap",alignItems:"center"}}>
                  <span style={{fontSize:9,padding:"2px 7px",borderRadius:10,background:chat.columnColor+"20",color:chat.columnColor,fontWeight:700}}>{chat.columnTitle}</span>
                  {chat.destination && <span style={{fontSize:9,padding:"1px 5px",borderRadius:10,background:T.panel,color:T.textSub}}>📍 {chat.destination}</span>}
                  {chat.visibleToAll && <span style={{fontSize:9,padding:"1px 5px",borderRadius:10,background:"#fef3c7",color:"#92400e",fontWeight:700}}>👥</span>}
                </div>
              </div>

              <div style={{display:"flex",flexDirection:"column",gap:4,flexShrink:0}} onClick={e=>e.stopPropagation()}>
                <button onClick={()=>setChatList(prev=>prev.map(c=>c.id===chat.id?{...c,unread:c.unread>0?0:1}:c))} title={chat.unread>0?"Marcar leído":"Marcar no leído"} style={{width:24,height:24,borderRadius:6,border:`1px solid ${T.border}`,background:"#fff",fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {chat.unread>0?"✓":"●"}
                </button>
                <button onClick={()=>setChatList(prev=>prev.map(c=>c.id===chat.id?{...c,tagged:!c.tagged}:c))} title="Marcar pendiente" style={{width:24,height:24,borderRadius:6,border:`1px solid ${T.border}`,background:chat.tagged?"#fef3c7":"#fff",fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  ⏳
                </button>
              </div>
            </div>
          );})}
        </div>
      </div>

      {/* Chat */}
      {selected ? (
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"#f8fafc"}}>
          <div style={{background:"#fff",padding:"10px 20px",borderBottom:"1px solid #e2e8f0",flexShrink:0}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:40,height:40,borderRadius:"50%",background:selected.columnColor+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:800,color:selected.columnColor}}>
                  {selected.name.split(" ").map(n=>n[0]).join("").slice(0,2)}
                </div>
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <p style={{margin:0,fontWeight:800,fontSize:14,color:"#1e293b"}}>{selected.name}</p>
                    <span style={{fontSize:10,padding:"1px 6px",borderRadius:10,background:CANAL_COLORS[selected.canal]+"22",color:CANAL_COLORS[selected.canal],fontWeight:700}}>{CANAL_ICONS[selected.canal]} {selected.canal}</span>
                  </div>
                  <div style={{display:"flex",gap:10,marginTop:3,flexWrap:"wrap"}}>
                    {selected.destination && <span style={{fontSize:10,color:"#475569"}}>📍 {selected.destination}</span>}
                    {selected.fechas      && <span style={{fontSize:10,color:"#475569"}}>📅 {selected.fechas}</span>}
                    {selected.adultos>0   && <span style={{fontSize:10,color:"#475569"}}>👤 {selected.adultos} adultos{selected.ninos>0?`, ${selected.ninos} niños`:""}</span>}
                  </div>
                </div>
              </div>
              <div style={{display:"flex",gap:8}}>
                <span style={{fontSize:11,padding:"3px 10px",borderRadius:20,background:selected.columnColor+"18",color:selected.columnColor,fontWeight:700}}>{selected.columnTitle}</span>
                <button onClick={()=>setShowFicha(true)} style={{background:"#eef2ff",border:"none",borderRadius:8,padding:"6px 14px",fontSize:12,color:"#6366f1",cursor:"pointer",fontWeight:700}}>Ver ficha →</button>
              </div>
            </div>
          </div>

          <div style={{flex:1,overflowY:"auto",padding:"16px 24px",display:"flex",flexDirection:"column",gap:10}}>
            {messages.map(msg=>{
              const isOut = msg.from==="agent"||msg.from==="bot";
              return (
                <div key={msg.id} style={{display:"flex",justifyContent:isOut?"flex-end":"flex-start",gap:8,alignItems:"flex-end"}}>
                  {!isOut && <div style={{width:28,height:28,borderRadius:"50%",background:"#6366f122",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#6366f1",flexShrink:0}}>{selected.name[0]}</div>}
                  <div style={{maxWidth:"60%"}}>
                    {isOut&&msg.from==="agent"&&<p style={{margin:"0 0 3px",fontSize:10,color:"#94a3b8",textAlign:"right",fontWeight:600}}>{msg.name}</p>}
                    <div style={{background:isOut?"#dcf8c6":"#fff",borderRadius:isOut?"16px 4px 16px 16px":"4px 16px 16px 16px",padding:"10px 14px",boxShadow:"0 1px 2px rgba(0,0,0,0.08)",fontSize:13,color:"#1e293b",lineHeight:1.5}}>
                      {msg.file ? (
                        msg.file.type.startsWith("image/") ?
                          <img src={msg.file.url} alt={msg.file.name} style={{maxWidth:200,borderRadius:8,display:"block"}} /> :
                          <a href={msg.file.url} download={msg.file.name} style={{display:"flex",alignItems:"center",gap:8,color:"#6366f1",textDecoration:"none",fontSize:12}}><span style={{fontSize:20}}>📄</span>{msg.file.name}</a>
                      ) : msg.text}
                    </div>
                    <p style={{margin:"3px 4px 0",fontSize:10,color:"#94a3b8",textAlign:isOut?"right":"left"}}>{msg.time}</p>
                  </div>
                  {isOut && <div style={{width:28,height:28,borderRadius:"50%",background:ROLE_COLORS[user.rol]+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:ROLE_COLORS[user.rol],flexShrink:0}}>{user.nombre?.split(" ").map(n=>n[0]).join("").slice(0,2)}</div>}
                </div>
              );
            })}
            <div ref={chatEndRef} />
          </div>

          <div style={{background:"#fff",padding:"12px 16px",display:"flex",gap:10,alignItems:"flex-end",borderTop:"1px solid #e2e8f0",flexShrink:0}}>
            <input type="file" ref={fileInputRef} accept="image/*,.pdf" onChange={handleFile} style={{display:"none"}} />
            <textarea value={newMsg} onChange={e=>setNewMsg(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();handleSend();}}} placeholder="Escribe un mensaje..." rows={1} style={{flex:1,padding:"10px 14px",borderRadius:12,border:"1px solid #e2e8f0",fontSize:13,resize:"none",outline:"none",fontFamily:"inherit",lineHeight:1.5,background:"#f8fafc",boxSizing:"border-box"}} />
            <div style={{display:"flex",gap:6,flexShrink:0}}>
              <button onClick={()=>fileInputRef.current?.click()} title="Adjuntar imagen o PDF" style={{background:"#f1f5f9",border:"none",borderRadius:10,padding:"10px 12px",cursor:"pointer",fontSize:16}}>📎</button>
              <button style={{background:"#f1f5f9",border:"none",borderRadius:10,padding:"10px 12px",cursor:"pointer",fontSize:16}}>😊</button>
              <button onClick={handleSend} style={{background:"#25d366",border:"none",borderRadius:10,padding:"10px 16px",cursor:"pointer",fontSize:16}} onMouseEnter={e=>e.currentTarget.style.background="#1da851"} onMouseLeave={e=>e.currentTarget.style.background="#25d366"}>➤</button>
            </div>
          </div>
        </div>
      ) : (
        <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:12,background:"#f8fafc",color:"#94a3b8"}}>
          <span style={{fontSize:48}}>💬</span>
          <p style={{fontSize:15,fontWeight:700,color:"#64748b",margin:0}}>Selecciona un chat</p>
          <p style={{fontSize:13,margin:0}}>Elige una conversación de la lista</p>
        </div>
      )}
    </div>
  );
}


// ─── PLACEHOLDER ──────────────────────────────────────────────────────────────

function Placeholder({ label }) {
  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, color: "#94a3b8", background: "#f1f5f9" }}>
      <span style={{ fontSize: 48 }}>🚧</span>
      <p style={{ fontSize: 15, fontWeight: 700, color: "#64748b", margin: 0 }}>{label}</p>
      <p style={{ fontSize: 13, margin: 0 }}>Próximamente</p>
    </div>
  );
}

// ─── APP PRINCIPAL ────────────────────────────────────────────────────────────

export default function App() {
  const [user, setUser]     = useState(null);
  const [active, setActive] = useState("kanban");
  const [checking, setChecking] = useState(true);
  const [unreadCount] = useState(0);

  // Verificar si hay sesión activa al cargar
  useEffect(() => {
    if (auth.isLoggedIn()) {
      auth.me()
        .then(u => setUser(u))
        .catch(() => {})
        .finally(() => setChecking(false));
    } else {
      setChecking(false);
    }
  }, []);

  if (checking) return (
    <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: T.bg }}>
      <Spinner />
    </div>
  );

  if (!user) return <Login onLogin={setUser} />;

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", height: "100vh", display: "flex", overflow: "hidden" }}>
      <Sidebar active={active} setActive={setActive} user={user} unreadCount={unreadCount} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Topbar */}
        <div style={{ background: "#fff", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 52, flexShrink: 0, borderBottom: `1px solid ${T.border}` }}>
          <span style={{ color: T.textPrimary, fontWeight: 800, fontSize: 16, letterSpacing: -0.3 }}>
            {NAV.find(n => n.id === active)?.label}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: ROLE_COLORS[user.rol]+"18", color: ROLE_COLORS[user.rol], fontWeight: 700 }}>
              {ROLE_ICONS[user.rol]} {user.rol}
            </span>
            <span style={{ fontSize: 13, color: T.textSub, fontWeight: 500 }}>{user.nombre}</span>
            <button onClick={async () => { await auth.logout(); setUser(null); }} style={{ background: T.panel, border: `1px solid ${T.border}`, color: T.textSub, padding: "5px 14px", borderRadius: 10, fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Salir</button>
          </div>
        </div>

        {/* Vistas */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {active === "kanban" && <KanbanView user={user} />}
          {active === "stats"  && <StatsView user={user} />}
          {active === "chats"  && <ChatsView user={user} />}
          {active === "config" && <ConfigPanel userRole={user.rol} userId={user.id} />}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;0,9..40,900&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-thumb { background: #d6d3d1; border-radius: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        select { font-family: 'DM Sans', sans-serif; }
        input, textarea, button { font-family: 'DM Sans', sans-serif; }
      `}</style>
    </div>
  );
}
